/* Source of truth since 2026-10-03 (ADR 0013): edit this file. The quiz-v1 HTML prototype is history. */
/* quiz-core.js: the pure core of the Stocked quiz. Every number the customer sees comes from here
   (ADR 0003), computed from the quiz state S and the dish list, with no DOM and no globals.

   Two adapters share this seam: index.html (window.QuizCore) and node --test (module.exports).

   Vocabulary is CONTEXT.md's: Block, Pack, Plate, Pairing, Dinner, Household, Box, Minimum, Coverage,
   Nudge, Spare, Footprint. The modules, in order:
     state      defaults(), SCHEMA_VERSION, normalise(raw, dishes), ensureReasonOrder(S) (OLE-82)
     household  household(S), dinnerFor(S): the one owner of the Plate
     pool       pool(S, dishes), kidPool(S, list), pairingOptions(S, dishes)
     composer   duelSets(S, dishes), composeMenu(S, dishes): the one owner of the diet rule
     frequency  frequencyPlan(S, packs): the target Box from the answers, the Frequency from the Box (2026-09-23)
     quote      openBox(S, dishes), quote(S, dishes): price, Coverage, Spare, Nudge, plan, one object
     flow       flow(S), markSteps(S)
     copy       tableLine, footprintLine, NUM, gbp

   V1 FORK (prototypes/quiz-v1, 2026-09-21, brief docs/internal/prd/quiz-v1-flow-2026-09-21.md). Two rules
   changed and nothing else was rewritten: a Plate is FOUR Blocks for every person at the table, kids
   included and whatever the Pairing (so the Pairing screen, the optimiser and the Dinner menu are out of
   the flow), and the Box sheet carries the frequency recommendation below. Parked code is left in place. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.QuizCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
'use strict';

/* ------------------------------------------------------------------ constants */
/* lp-quiz-sync: in the Shopify build the pack prices come from the products. lp-quiz-boot.js sets
   window.lpQuiz.prices from the section payload (the builder card's own two prices) before this file runs. */
const LPQ_PRICES = (typeof self !== 'undefined' && self.lpQuiz && self.lpQuiz.prices) || {};
if (!(LPQ_PRICES.sub > 0 && LPQ_PRICES.once > 0)) throw new Error('lp-quiz-core: window.lpQuiz.prices is not set; lp-quiz-boot.js must run first');
const PACK_PRICE = LPQ_PRICES.sub, ONE_OFF_PACK_PRICE = LPQ_PRICES.once, BLOCKS_PER_PACK = 8;
/* OLEO-154 (Tim, 6 Oct: "in terms of having the changes exclusively in the template, make sure we do that"): the
   template the page runs, from the section's v11_variant (lp-quiz-boot.js copies it to window.lpQuiz.variant before
   this file loads, as it does the prices). Templates can't hold a dot, so the names are V2A and V2B:
     "a"  templates/page.lp-quiz-v2a.json (?view=lp-quiz-v2a): the milestone stepper on the menu
     "b"  templates/page.lp-quiz-v2b.json (?view=lp-quiz-v2b): the box-offer screen and the offer cards
     ""   anything else, the section default "off" included: today's templates/page.lp-quiz.json, exactly as live
   Both a and b also get every shared V1.1 change: OLEO-155 (For you 16, Full Menu unfiltered), OLEO-159 (the cart
   replaces the quiz's earlier box), OLEO-162 (the versatile screen) and the frequency cards' "per person". Every one
   is read through v11(); with "" each code path is the live theme's. */
const V11_VARIANTS = Object.freeze(['a', 'b']);
/* OLEO-220 (Tim, 19 Oct): "v3" is templates/page.lp-quiz-v3.json (?view=lp-quiz-v3), Quiz V3 = Quiz V2 (V2A) plus the
   Email capture screen. v11() reads it as "a", so every V1.1 and V2A path runs on V3 exactly as on V2; only isV3()
   and the page's version (PAGE_VARIANT: flow, cohortAttributes, quizVersion) tell the two apart. */
const V3_VARIANT = 'v3';
const isV3 = raw => String(raw == null ? '' : raw).trim().toLowerCase() === V3_VARIANT;
const v11 = raw => { const v = String(raw == null ? '' : raw).trim().toLowerCase(); if (v === V3_VARIANT) return 'a'; return V11_VARIANTS.includes(v) ? v : ''; };
const VARIANT = v11(typeof self !== 'undefined' && self.lpQuiz ? self.lpQuiz.variant : '');
/* the page's template as a version key: "v3" on Quiz V3, else VARIANT (so off V3 it is VARIANT exactly) */
const PAGE_VARIANT = isV3(typeof self !== 'undefined' && self.lpQuiz ? self.lpQuiz.variant : '') ? V3_VARIANT : VARIANT;
/* the version name. OLEO-178 (Tim, 7 Oct): Quiz V2 is "v2" (preview events before 7 Oct said "v2a"; the data layer
   reads them as "v2"). V2B is dropped and keeps "v2b", so a stray V2B visit never counts as V2. OLEO-179: it is
   carried only as `quiz` (quizVersion), on the order (cohortAttributes) and on every Quiz V2 event (lp-quiz-boot.js
   track()); there is no quiz_variant. */
const VARIANT_NAMES = Object.freeze({ a: 'v2', b: 'v2b' });
const variantName = v => isV3(v) ? 'v3' : VARIANT_NAMES[v11(v)] || '';   // OLEO-220: Quiz V3 is "v3"
// 9.50 is the Subscribe & Save pack price, 11.05 the one-time price (live builder: £38 vs £44.20 for 4).
/* MILESTONES (Tim, 2026-09-24; CONTEXT.md "Milestone"): the Pack counts at which the Box gains something,
   the one owner of the 4, the 6, the 8 and the 10%. Stocked's own, read off the live builder's settings in
   clients/stocked/templates/product.dynamic-build-a-bundle.json, never invented:
     start     line 235       minimum_required: 4             (minimum_leadup_message "to start your box")
     delivery  line 241       free_delivery_number: 6         (delivery_leadup_message "to get free delivery")
     discount  lines 247-248  skio_discount_number: 8,
                              discount_percentage: 10         (the Build a Box Discount, 10% off both prices,
                                                               ~~£79.56~~ £68.40; the cart applies it per line)
   The builder's own discount copy says "Save 20%" (skio_reward_threshold_msg), which does not match its
   discount_percentage; the quiz follows the number. The last Milestone is also the most a Box holds.
   MIN_PACKS and BOX_DISCOUNT are derived from it; milestoneFor() writes the sticky panel's row from it. */
const MILESTONES = (() => {
  const pct = 10;                                      // discount_percentage, line 248
  return Object.freeze([
    { id: 'start',    packs: 4, emoji: '\u{1F4E6}',          goal: 'to start your box' },            // 📦 minimum_required, line 235; the builder's own word (OLE-71, Tim 29 Sep, reversing OLE-60's "basket")
    { id: 'delivery', packs: 6, emoji: '\u{1F69A}',          goal: 'to get free delivery' },         // 🚚 free_delivery_number, line 241
    { id: 'discount', packs: 8, emoji: '\u{1F3F7}\u{FE0F}', goal: `to get an extra ${pct}% off`, pct }, // 🏷️ skio_discount_number, line 247
    { id: 'done', emoji: '\u{1F389}', text: `Free delivery and an extra ${pct}% off.` }            // 🎉 every Milestone reached
  ]);
})();
const milestone = id => MILESTONES.find(m => m.id === id);
const MIN_PACKS = milestone('start').packs;            // Stocked's real minimum order, where every Box opens (ADR 0003)
/* The Build a Box Discount: quote() reads it, and any offer code applies after it. */
const BOX_DISCOUNT = { packs: milestone('discount').packs, pct: milestone('discount').pct };
const penny = x => Math.round(x * 100) / 100;
/* Amended 2026-09-05 (Tim): a section whose Dinner needs more than one Pack opens at TWO Packs of each of its
   dishes, so every Dinner comes from one dish. A big table therefore opens at six Packs (three dishes, two each). */
const BIG_TABLE_DISHES = 3;
const CAP = { tiny: 4, drawer: 8, half: 16, chest: 24 };   // CAP.tiny == MIN_PACKS, so that box is always exactly the minimum
const BLOCK_PRICE = PACK_PRICE / BLOCKS_PER_PACK;
/* The Pairings the catalogue carries (dishes.js b: field). A Plate is counted in Blocks by its Pairing
   (CONTEXT.md): 2 in a wrap, 3 over rice or pasta, 4 on its own. A loaded jacket potato is a Pairing at
   three Blocks like rice, not a four-Block plate; "on its own" means nothing under the Blocks at all
   (Tim, 2026-09-22). The catalogue has no jacket key, so a jacket rides with the rice/standalone dishes.
   "standalone" is on every dish. */
const PAIRINGS = [['rice', 'Rice'], ['wrap', 'Wraps or flatbreads'], ['pasta', 'Pasta'], ['standalone', 'On its own']];
const PAIRING_KEYS = PAIRINGS.map(([k]) => k);
const PLATE_FOR = { wrap: 2, rice: 3, pasta: 3, standalone: 4 };
/* V1: the Plate is four Blocks for everyone at the table, kids included (Tim, 2026-09-21). PLATE_FOR is kept
   for the parked Pairing screen and the parked Dinner menu; nothing in the V1 flow reads it. */
const V1_PLATE = 4;
/* OLE-68 (Tim, 29 Sep, grill Q1, Q14, Q15, Q19; Charlie's Scenario 2, capture.md F05): a meal is 4 Blocks a grown-up
   and 2 a kid, whether the kids eat the same food or have their own menu. It counts the MEALS: the frequency
   cards' meal count and the recommendation (frequencyPlan's `dinners`), the order summary's meal line
   (mealsFor), and the Coverage the quote lays (dinnerFor). It never sizes the TARGET box, the progress bar's goal:
   that keeps V1_PLATE for everyone ("Don't change the progress bar", Q19), so frequencyPlan's `rate` and packsFor
   are untouched. KID_BLOCKS is the kid's share; blocksPerMeal(H) the table's Blocks a meal. */
const KID_BLOCKS = 2;
const blocksPerMeal = H => H.adults * V1_PLATE + H.kids * KID_BLOCKS;
/* ------------------------------------------------------ Intention and Frequency (Candidate A, 2026-09-22)
   Two glossary terms carry this (clients/stocked/CONTEXT.md). INTENTION is how often the Household plans
   to cook a Dinner from Stocked, asked in their own words and never inferred. FREQUENCY is how often the
   Box is delivered, in weeks, always a plan Stocked actually sells.
   Both tables carry `live` flags rather than being edited when the offer changes: a plan that comes off
   sale is flipped, not deleted, so the row that proves the rule still reads. */
/* Amended 2026-09-23 (Tim, from the 22 Sep review, CONTEXT.md Intention): "How many meals a week do you
   want from Stocked?" 1 to 5, or "Less often". `id` is what the state and the cohort tag carry; `perWeek`
   is the Dinners a week the rule multiplies by, and "less often" is read as one every two weeks (the
   architect note's 0.5). The 22 Sep three (twice / once a week / once a fortnight) are gone; a saved 0.5
   is read as 'less' by normalise().
   Reworded 2026-09-23 (Tim, from Charlie's comment): "How many days a week do you plan on using Stocked?"
   Same answers and the same rule: one answer is one table Dinner a day. `phrase` is the customer echo and
   the Klaviyo `quiz_intention_label` ("2 days a week", "less than once a week").
   C07 (Charlie's Paper comment "5+?", Tim 25 Sep, QZ-comments-research.md): the top answer reads "5+ days",
   because a seven-day week leaves the 6- and 7-day planner no true answer otherwise. The stored id and
   perWeek stay 5, so the Box maths does not move. ONE SOURCE for the words: `label` is the intention
   screen's row (index.html reads it) and `phrase` is the echo on the frequency card and the Klaviyo label;
   each label is the start of its phrase (walk-freq.js and quiz-core.test.js check it). */
/* OLE-68 (Charlie's Miro "Screen 14", capture.md F07; Tim 29 Sep, Q18): `card` is the recommended frequency
   card's echo, "based on your intended 2 days/week", "less than 1 day/week" for the least answer. */
const INTENTIONS = [
  { id: 1, perWeek: 1, label: '1 day', phrase: '1 day a week', card: '1 day/week' },
  { id: 2, perWeek: 2, label: '2 days', phrase: '2 days a week', card: '2 days/week' },
  { id: 3, perWeek: 3, label: '3 days', phrase: '3 days a week', card: '3 days/week' },
  { id: 4, perWeek: 4, label: '4 days', phrase: '4 days a week', card: '4 days/week' },
  { id: 5, perWeek: 5, label: '5+ days', phrase: '5+ days a week', card: '5+ days/week' },
  { id: 'less', perWeek: 0.5, label: 'Less than once a week', phrase: 'less than once a week', card: 'less than 1 day/week' }
];
const intentionOf = S => INTENTIONS.find(i => i.id === S.intention) || null;
/* `live` is what Stocked sells on Skio today (the standalone prototype's stand-in for the product's plans);
   `offered` (every 4, 6 or 8 weeks, Tim 2026-09-23) now sizes only the TARGET box, the progress bar's goal.
   OLE-17 R2 (Tim, 27 Sep): the cards are the SOLD plan that misses the fewest meals a delivery at the answer
   and the sold plans either side of it (rankByMiss, cardsFor, by frequencyPlan), and the "Select different
   frequency" link (OLE-16) lists the other sold ones.
   "Sold" is the product's own plans in the Shopify build (soldFrequencies), these `live` rows standalone.
   OLE-17 rework (Tim, 27 Sep): every 7 and every 8 weeks will be sold too (8 weeks from Mon 28 Sep, and 8 is
   the one that matters most), so the live rows run every 1 to 8 weeks. The Shopify build still reads the
   product's plans: until the product carries "Every 7 Weeks" / "Every 8 Weeks" the cards show 1 to 6, and
   the page load after it does, those plans appear.
   OLE-68 (Tim, 29 Sep, grill Q5: "we can design for one to six plus eight with no seven"): every 7 weeks is
   flipped off, so the standalone rows are 1 to 6 and 8. The Shopify build still reads the product's plans. */
const FREQUENCY_PLANS = [
  { weeks: 1, live: true }, { weeks: 2, live: true }, { weeks: 3, live: true },
  { weeks: 4, live: true, offered: true }, { weeks: 5, live: true }, { weeks: 6, live: true, offered: true },
  { weeks: 7, live: false },
  { weeks: 8, live: true, offered: true }
];
const PACK_CEILING = [
  { packs: 8,  live: true },
  { packs: 12, live: false }             // the wildcard for very large Households, not offered
];
const liveFrequencies = () => FREQUENCY_PLANS.filter(f => f.live).map(f => f.weeks).sort((a, b) => a - b);
const offeredFrequencies = () => FREQUENCY_PLANS.filter(f => f.offered).map(f => f.weeks).sort((a, b) => a - b);
/* OLE-16 (Tim, 27 Sep: yes, listing only plans Skio actually sells), amended by OLE-17 (27 Sep): the cards are
   the recommendation and the sold plans either side of it (R2); the "Select different frequency" link lists the rest.
     pickableFrequencies()  every gap the quiz keeps as a pick through normalise: any FREQUENCY_PLANS row. A
                            pick counts only while the product sells it (frequencyPlan's `chosen`, I2)
     planName(w)            Skio's name for a gap: "Every 1 Week", "Every N Weeks" (QZ-18's capture of the
                            build-a-bundle product, 23 Sep; lp-quiz-cart.js finds the plan by this name)
     planWeeks(name)        the inverse, null for any other name
     soldFrequencies(plans) the weeks the product sells: `plans` is the section payload's
                            window.lpQuiz.bundle.sellingPlans ([{ name }]) in the Shopify build. Not an array
                            (the standalone prototype), or no plan it names has a row here, the live rows above.
                            A plan with no row in FREQUENCY_PLANS is ignored, because normalise would not keep
                            the pick; give it a row first.
     otherFrequencies(plans, cards)
                            what the link lists: the sold plans that are not on a card, ascending. */
const pickableFrequencies = () => FREQUENCY_PLANS.map(f => f.weeks).sort((a, b) => a - b);
const planName = w => `Every ${w} ${w === 1 ? 'Week' : 'Weeks'}`;
const planWeeks = name => {
  const m = typeof name === 'string' ? /^Every (\d+) Weeks?$/.exec(name) : null;
  return m && +m[1] >= 1 && planName(+m[1]) === name ? +m[1] : null;
};
function soldFrequencies(plans) {
  const known = pickableFrequencies();
  const weeks = Array.isArray(plans) ? [...new Set(plans.map(p => planWeeks(p && p.name)))].filter(w => known.includes(w)) : [];
  return weeks.length ? weeks.sort((a, b) => a - b) : liveFrequencies();
}
function otherFrequencies(plans, cards) {
  const on = Array.isArray(cards) ? cards : [];
  return soldFrequencies(plans).filter(w => !on.includes(w));
}
const liveCeiling = () => Math.max(...PACK_CEILING.filter(c => c.live).map(c => c.packs));
/* The rule in two sentences (2026-09-23, Frequency after the packs). Exported so a wireframe reviewer,
   the Liquid schema's info text and this module cannot drift apart, with the worked examples below as
   data and a test asserting they match the function. */
const RULE_IN_TWO_SENTENCES =
  'Your answers set a target box: the longest of every 4, 6 or 8 weeks whose packs fit in the biggest box, never under 4 packs. ' +
  'Once the packs are picked, we recommend the plan Stocked sells whose weeks at the days a week you answered come closest to the meals in the box, show the plans either side of it, and you can choose another.';
/* [name, household, intention, target packs, target weeks, held]. Every row is also the recommendation
   when the box is filled to its target (the grid property the tests assert). */
const RULE_WORKED_EXAMPLES = [
  ['one grown-up, less often',          { adults: 1, kids: 0 }, 'less', 4, 8, false],   // the Minimum lifts it: 4 packs last 16 weeks
  ['a couple, once a week',             { adults: 2, kids: 0 }, 1,      8, 8, false],
  ['one grown-up, three a week',        { adults: 1, kids: 0 }, 3,      6, 4, false],   // every 6 weeks would need 9 packs
  ['two and a kid, less often',         { adults: 2, kids: 1 }, 'less', 6, 8, false],
  ['a family of five, less often',      { adults: 2, kids: 3 }, 'less', 8, 6, false],   // 7.5 rounds to 8; 8 weeks would need 10
  /* held: four at the table twice a week want 16 packs every 4 weeks; the box stops at 8 */
  ['two grown-ups and two kids, twice a week', { adults: 2, kids: 2 }, 2, 8, 4, true]
];
/* A Pairing can carry an allergen the dish does not: pasta, wraps and flatbreads are wheat. */
const PAIRING_ALLERGENS = { pasta: ['Gluten'], wrap: ['Gluten'] };
/* The reason answers' keys (the first question, "What got you interested in STOCKED?"): QZ-19's four outcomes
   (24 Sep) and OLE-7's spending (25 Sep). Their labels are copy and live in index.html PROBLEMS; the test holds
   the two lists equal. The keys are the saved state and the `quiz_reason` cohort value, so they never change. */
const REASON_KEYS = ['time', 'scratch', 'healthy', 'deciding', 'spending'];
const isReasonOrder = o => Array.isArray(o) && o.length === REASON_KEYS.length && REASON_KEYS.every(k => o.includes(k));
/* OLE-82 (Tim, 30 Sep): the answers are shown in a random order, against order bias (Andrea, 29 Sep). Drawn
   ONCE per visitor, uniformly (Fisher-Yates), and kept in S.reasonOrder: a saved order is returned as it is,
   so the list never reshuffles on Back, a reload or a resume. `rand` is injectable for the tests. */
function ensureReasonOrder(S, rand = Math.random) {
  if (isReasonOrder(S.reasonOrder)) return S.reasonOrder;
  const o = REASON_KEYS.slice();
  for (let i = o.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [o[i], o[j]] = [o[j], o[i]]; }
  S.reasonOrder = o;
  return o;
}
const HOUSEHOLDS = ['family', 'couple', 'solo', 'carer'];   // PARKED 2026-09-23: the old household types, read by normalise() only
const MAX_AT_TABLE = 10;                                    // the number pickers stop here, grown-ups and kids each (Tim, 2026-09-23: was 8)
const NUM = { 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve' };
const gbp = n => '£' + n.toFixed(2);

/* ---------------------------------------------------------------------- state */
/* The state's shape, and the only place it is declared. pairing is RANKED, favourite first (Tim,
   2026-09-05): the favourite sizes the grown-up Plate, so there is no separate plateAdult. */
function defaults() {
  return {
    /* The Household (2026-09-23): two number pickers, grown-ups (1+) and kids (0+, "12 and under").
       `adults` null means unanswered. `household` and `size` are DERIVED from the two counts by
       normalise() and kept only because parked screens and old board seeds read them; never asked. */
    adults: null, kids: 0,
    household: null, seen: null, size: 1, carerToo: false, carerPicked: false, problem: null,
    /* OLE-82 (Tim, 30 Sep: "shuffle the order"): the order this visitor sees the reason answers in, a
       permutation of REASON_KEYS drawn once by ensureReasonOrder() and saved with the rest of S, so Back,
       reload and resume show the same list. [] means not drawn yet. */
    reasonOrder: [],
    intention: null,                                   // days a week using Stocked: 1..5 or 'less' (CONTEXT.md, Intention)
    frequency: null,                                   // the Frequency the customer picked, in weeks; null means the recommendation stands
    plateKid: 'half',                                  // the kids' Plate against the grown-ups': 'same', 'less' or 'half'. PARKED 2026-09-05: never asked, always 'half'
    /* diet (2026-09-23): 'meat' | 'plant' | 'mix' (both ticked). A multi-select stored as one string so the
       key keeps its type; the old 'veg' and 'vegan' are read as 'plant' (every plant dish is vegan). */
    diet: null, gf: false, allergens: [], conditions: [],
    /* The grown-ups' slider (and a shared table's one slider) opens at "A bit spicy": Charlie C10, Tim 25 Sep;
       replaces Tim's 6 Sep Medium default. Since 24 Sep a shared family table answers once for everyone, so an
       untouched Medium told the menu the kids eat Medium. A split table's kids' slider still opens at not spicy. */
    heatAdult: [0, 1], heatKids: [0], freezer: null,
    pairing: [],                                       // ranked Pairing keys, favourite first
    kidsSplit: null,
    email: null, offer: null,                          // 008 inline save; 002 ad offer from the URL
    scale: null, nights: null, cook: null, fi: 2,      // fi = featured-in index; 2 is Dragons' Den, the homepage default
    duels: [], kidDuels: [],                           // dish NAMES (or null for "neither"), never dish objects
    box: {},                                           // dish name → packs
    purchase: 'sub', sheetOpen: false, qtyOpen: -1, boxTouched: false
  };
}
/* The version is derived from the shape, so a shape change invalidates saved state without anyone
   remembering to bump a number. SCHEMA_SALT is for semantic changes that keep the keys (a string that
   became an array): bump it in that case only. Bumped 25 Sep for C10: a saved heatAdult of [0, 1, 2] cannot say
   whether it was chosen or the old Medium default left untouched, so saved state from before is dropped. */
const SCHEMA_SALT = 'c10-heat-default-2026-09-25';
const SCHEMA_VERSION = (() => {
  const d = defaults();
  const sig = Object.keys(d).sort().map(k => k + ':' + (Array.isArray(d[k]) ? 'array' : typeof d[k])).join('|') + '|' + SCHEMA_SALT;
  let h = 0; for (let i = 0; i < sig.length; i++) h = (h * 31 + sig.charCodeAt(i)) | 0;
  return 'v' + (h >>> 0).toString(36);
})();
const isObj = x => x && typeof x === 'object' && !Array.isArray(x);
const asArray = x => Array.isArray(x) ? x : (x == null || x === '' ? [] : [x]);
const int = (x, d) => Number.isFinite(+x) ? Math.round(+x) : d;
/* normalise: any raw shape (a saved payload of any version, a preset, a URL) into a valid S. Unknown keys
   are dropped, wrong shapes are coerced, impossible values fall back to the default. Every writer goes
   through here, so a stale or wrong-shaped input degrades to defaults instead of a blank page. */
function normalise(raw, dishes) {
  const S = defaults(), r = isObj(raw) ? raw : {};
  const names = new Set((dishes || []).map(d => d.n));
  const nameOrNull = w => { const n = w && typeof w === 'object' ? w.n : w; return typeof n === 'string' && names.has(n) ? n : null; };
  for (const k of Object.keys(S)) if (k in r) S[k] = r[k];
  S.seen = ['new', 'returning'].includes(S.seen) ? S.seen : null;
  S.reasonOrder = isReasonOrder(S.reasonOrder) ? S.reasonOrder.slice() : [];   // OLE-82: a whole permutation or not drawn yet
  /* 1..5 or 'less'. A number-like string ('3') is read as the number; a saved 0.5 (the 22 Sep fortnightly
     answer) is 'less'. NOT int() on the way in: it rounds, and 0.5 would silently become 1. */
  S.intention = +S.intention === 0.5 ? 'less'
              : INTENTIONS.some(i => i.id === S.intention) ? S.intention
              : INTENTIONS.some(i => i.id === +S.intention && S.intention !== '' && S.intention !== null) ? +S.intention : null;
  S.frequency = pickableFrequencies().includes(int(S.frequency, 0)) ? int(S.frequency, 0) : null;   // a card or a sold gap (OLE-16)
  S.carerToo = !!S.carerToo; S.carerPicked = !!S.carerPicked;
  /* The Household. New shape: `adults` and `kids`, counted. Old shape (a board seed, a saved payload, a
     test fixture): `household` + `size` + `kids`, read into the counts so it keeps meaning what it meant. */
  const clampN = (x, lo, d) => Math.max(lo, Math.min(MAX_AT_TABLE, int(x, d)));
  S.kids = clampN(S.kids, 0, 0);
  if ('adults' in r && r.adults != null) S.adults = clampN(r.adults, 1, 1);
  else if (HOUSEHOLDS.includes(r.household)) {
    const size = Math.max(1, int(r.size, 4));
    if (r.household === 'family') { S.kids = Math.min(S.kids, size - 1); S.adults = Math.max(1, size - S.kids); }
    else { S.kids = 0; S.adults = r.household === 'couple' || (r.household === 'carer' && S.carerToo) ? 2 : 1; }
  } else S.adults = null;
  /* derived, never asked: the old labels the parked screens still read */
  S.size = (S.adults || 1) + S.kids;
  S.household = S.adults == null ? null : S.kids > 0 ? 'family' : S.adults === 1 ? 'solo' : S.adults === 2 ? 'couple' : 'family';
  S.plateKid = ['same', 'less', 'half'].includes(S.plateKid) ? S.plateKid : 'half';   // the old numeric 2 reads as 'half'
  S.diet = ['veg', 'vegan'].includes(S.diet) ? 'plant' : ['meat', 'plant', 'mix'].includes(S.diet) ? S.diet : null;
  S.allergens = asArray(S.allergens).filter(a => typeof a === 'string');
  S.conditions = asArray(S.conditions).filter(a => typeof a === 'string');
  S.gf = S.allergens.includes('Gluten');                 // gluten is a requirement; the chip is the source of truth
  const heat = a => { const v = [...new Set(asArray(a).map(n => int(n, -1)).filter(n => n >= 0 && n <= 3))]; return v.length ? v : [0]; };
  S.heatAdult = heat(S.heatAdult); S.heatKids = heat(S.heatKids);
  S.freezer = S.freezer in CAP ? S.freezer : null;
  S.pairing = [...new Set(asArray(S.pairing).filter(k => PAIRING_KEYS.includes(k)))];
  S.kidsSplit = ['same', 'own'].includes(S.kidsSplit) ? S.kidsSplit : null;
  S.email = typeof S.email === 'string' && S.email ? S.email : null;
  S.offer = isObj(S.offer) && typeof S.offer.code === 'string' && int(S.offer.pct, 0) > 0 && int(S.offer.pct, 0) < 100 ? { code: S.offer.code.toUpperCase(), pct: int(S.offer.pct, 0) } : null;
  S.scale = S.scale == null ? null : int(S.scale, null); S.nights = S.nights == null ? null : int(S.nights, null);
  S.cook = [15, 30, 60, 120].includes(int(S.cook, 0)) ? int(S.cook, 0) : null;
  S.fi = Math.max(0, int(S.fi, 2));
  S.duels = asArray(S.duels).map(nameOrNull); S.kidDuels = asArray(S.kidDuels).map(nameOrNull);
  const box = {}; if (isObj(S.box)) for (const [n, q] of Object.entries(S.box)) { const v = int(q, 0); if (v > 0 && names.has(n)) box[n] = v; }
  S.box = box;
  S.purchase = S.purchase === 'once' ? 'once' : 'sub';
  S.sheetOpen = false; S.qtyOpen = -1;                   // transient UI state never survives a load
  S.boxTouched = !!S.boxTouched && Object.keys(box).length > 0;
  /* A shared table answers the spice question once (Tim, 24 Sep): the kids' list mirrors the grown-ups',
     so a kids' value left over from an earlier split answer cannot survive a load. */
  if (household(S).sharedTable) S.heatKids = S.heatAdult.slice();
  return S;
}

/* ------------------------------------------------------------------ household */
/* The Household: the two counts the customer picked (2026-09-23). Unanswered is zero people, which is
   what keeps frequencyPlan() null until the question is answered. */
function household(S) {
  const answered = S.adults != null;
  const adults = answered ? S.adults : 0, kids = answered ? S.kids : 0;
  return { isFamily: kids > 0, adults, kids, people: adults + kids, splitKids: kids > 0 && S.kidsSplit === 'own',
    sharedTable: kids > 0 && S.kidsSplit === 'same', answered };
}
/* THE SHARED-TABLE SPICE RULE (Tim, 24 Sep: "if the user answers that everyone has the same meal, then
   there doesn't need to be the split for the grown-ups and for the kids"). A table with kids who eat the
   same meal is asked ONE spice question, for everyone, and that answer (S.heatAdult) is the kids' answer
   too: every reader of the kids' spice goes through kidsHeat(), never S.heatKids directly. A split table
   keeps its two sliders; a table with no kids has only the grown-ups' slider, as before. */
function kidsHeat(S) { return household(S).sharedTable ? S.heatAdult : S.heatKids; }
/* The slider's write: the list 0..n, as the core reads it. On a shared table the one answer is written to
   both lists, so the state never holds a kids' value the customer was not shown. */
function setHeat(S, key, n) {
  const v = Math.max(0, Math.min(3, parseInt(n, 10) || 0)), list = [0, 1, 2, 3].slice(0, v + 1);
  S[key] = list;
  if (key === 'heatAdult' && household(S).sharedTable) S.heatKids = list.slice();
  return S;
}
/* The kidsplit answer. Same: the kids' spice becomes the table's, because the table now answers once for
   everyone and the kids' own answer no longer applies (Tim, 24 Sep). Own: the kids KEEP the spice they had
   (Tim's Q25 decision B, 25 Sep: "a kids-question change keeps the kids' spice unless it no longer
   applies"). That replaces Tim's 24 Sep rule, which reopened the kids' slider at not spicy on every change to
   Own. A first answer of Own finds the default, not spicy (defaults()). Re-picking the held answer changes
   nothing. The duels are cleared by applyAnswer (the split decides how many of each kind). */
function setKidsSplit(S, v) {
  S.kidsSplit = v === 'same' || v === 'own' ? v : null;
  if (S.kidsSplit === 'same') S.heatKids = S.heatAdult.slice();
  return S;
}
/* The Box keeps only what the order summary can list: a dish a later answer took off the menu leaves the
   Box, every other pack stays. menuLists ignores spice, so in V1 only a diet change (or a parked allergen)
   drops anything. The Kids range stays listable whatever the table (the All tab's Kids' menu), so a change of
   headcount drops nothing. Called by applyAnswer and by index.html pruneBox(). Returns the dishes dropped. */
function keepOnMenu(S, dishes) {
  const L = menuLists(S, dishes, { dietGate: true }), ok = new Set([...L.table, ...L.kids, ...(L.kidsRange || [])].map(d => d.n));   // the diet gate holds here (OLE-58 rework): a switch to Plant-based still drops the meat packs
  const dropped = Object.keys(S.box || {}).filter(n => !ok.has(n));
  dropped.forEach(n => { delete S.box[n]; });
  return dropped;
}
/* ANSWERS: the one owner of what an answer changes besides itself.
   A RE-PICK OF THE HELD ANSWER CHANGES NOTHING, on every screen (qz-verify baseline, 25 Sep). The caller
   still moves the quiz on (index.html pick()).
   A CHANGED ANSWER KEEPS THE BOX (Tim's Q25 decision B, 25 Sep: "Back everywhere, and changing an answer
   keeps the box"). Coverage, the milestone messages and the For you picks are read from the answers at
   every render, so they follow the new answer with nothing reset here.
     adults, kids, kidsSplit, intention
                    a Box the customer has touched stands. Only an untouched Box re-opens at the new answers'
                    defaults (empty in V1: the order summary opens empty).
     kidsSplit      also: the kids' spice per setKidsSplit (kept, unless the table now answers for everyone),
                    and the parked duels are cleared.
     diet (any answer, when the caller passes the dishes)
                    keepOnMenu drops only the packs of dishes the new answer takes off the menu.
     problem        the parked scale ladder (scale, nights).
   Before 25 Sep a new headcount or kids answer emptied the Box, and the kids' slider reopened at not spicy.
   Returns what moved besides the answer: [] for a re-pick; 'box' when an untouched Box re-opens; 'duels';
   'heatKids'; 'scale'; 'dropped:<dish>' for each pack a new diet took off the menu. */
function applyAnswer(S, key, value, dishes) {
  if (S[key] === value) return [];
  const reset = [];
  if (key === 'kidsSplit') {
    const kidsHeat = S.heatKids.join();
    setKidsSplit(S, value);
    S.duels = []; S.kidDuels = []; reset.push('duels');
    if (S.heatKids.join() !== kidsHeat) reset.push('heatKids');
  } else S[key] = value;
  if (['adults', 'kids', 'kidsSplit', 'intention'].includes(key) && !S.boxTouched) { if (Object.keys(S.box || {}).length) reset.push('box'); S.box = {}; }
  if (key === 'problem') { S.scale = null; S.nights = null; reset.push('scale'); }
  if (dishes) keepOnMenu(S, dishes).forEach(n => reset.push('dropped:' + n));
  return reset;
}
/* Packs a tap adds: ONE, for every table and every list (Tim's correction, 2026-09-23). The earlier rule,
   ceil(people / 2) packs a tap on a shared table, was our mistake: the customer reads "1 pack feeds 2
   people" and sizes the Box, one pack at a time. The row's +, the popup's pill and the Box bar all go
   through tapBox, which reads this. */
const PACKS_PER_TAP = 1;
function packsPerTap(S, section) { return PACKS_PER_TAP; }
/* Packs of a dish one meal takes (renamed from packsPerTap, 2026-09-23): one Pack feeds two people (two
   4-Block Plates), so a table of N takes ceil(N / 2) Packs of a dish for one meal; a split table's two
   sections each count their own people. This is the SHAPE of the composed Box (boxPlan, the parked prefill
   and tasting, ADR 0003's two-packs-a-dish), never what a tap adds. */
function packsPerDish(S, section) {
  const H = household(S);
  const n = section === 'kids' ? H.kids : H.splitKids ? H.adults : H.people;
  return Math.max(1, Math.ceil(n / 2));
}
/* The Dinner, and the one owner of the Plate: the favourite Pairing sizes the grown-up Plate; the kids'
   Plate is stated against it (the same, one Block fewer, about half; Tim, 2026-09-05, no glyphs, no separate
   screen). Before the Pairing screen the standard plate (three over rice) stands in. */
const KID_PLATE = { same: a => a, less: a => Math.max(1, a - 1), half: a => Math.max(1, Math.round(a / 2)) };
/* V1 (Tim, 2026-09-21): every Plate is V1_PLATE Blocks, grown-up and kid alike, so a table of N eats 4N
   Blocks a Dinner. KID_PLATE and the favourite Pairing are parked with their screens, not deleted: the
   favourite is still reported so the parked menu keeps rendering.
   OLE-68 (Tim, 29 Sep): a kid's Plate is KID_BLOCKS (2), so a Dinner is blocksPerMeal(H), 4 a grown-up and 2 a
   kid, and the Coverage the quote lays (sections, dinnerPlan) counts at it, split table or shared. */
function dinnerFor(S) {
  const H = household(S);
  const favourite = S.pairing[0] || 'rice';
  const adultPlate = V1_PLATE, kidPlate = KID_BLOCKS;
  return { favourite, ranking: S.pairing.slice(), adultPlate, kidPlate, blocks: blocksPerMeal(H), adults: H.adults, kids: H.kids };
}

/* ----------------------------------------------------------------------- pool */
const maxHeat = S => Math.max(...S.heatAdult, ...(household(S).kids ? kidsHeat(S) : [0]));
/* The GATES, in one place (2026-09-22). A gate removes a dish from consideration entirely; everything
   else is a weight in score() below. Reading order matters only for the comment: they all must pass.
     available      out of stock is out of the tasting AND out of the fill. An award on an out-of-stock
                    dish counts for nothing here (Tim).
     builder        listed on the live Build a Bundle (BUILDER_LISTED, captured 24 Sep), or it is not offered.
     diet           plant-based -> plant dishes only (2026-09-23). A meat PREFERENCE is not a gate,
                    it is the meat term in the score, which is how a meat table still meets one plant dish.
     allergens      as today: a declared allergen, and Gluten via the gf flag.
     spice band     asymmetric, and it REPLACES the old `d.s > mh` ceiling: the chosen level, one above
                    and one below — except at level 0, where "not spicy" stays a ceiling and one above is
                    never offered (Tim).
   `opts.heat` overrides the table's ceiling so the kids' tasting can be gated at the kids' answer. */
/* Tim's rule, 2026-09-22: "show that level or one above or one below, and go down a level for something
   really good". So the only HARD limit is the top — one above what they chose, and a chosen 0 stays a
   ceiling at 0 because "not spicy" means not spicy. Everything at or below is eligible; how close it sits
   to the level they chose is a PREFERENCE in score(), which is what lets an award or a 4.6+ rating lift a
   much milder dish. Before this the band gated below as well, which left a vegan table at the default
   spice with four dishes out of twenty-three. */
const spiceBand = (level, dishSpice) => level === 0 ? dishSpice === 0 : dishSpice <= level + 1;
/* BUILDER-LISTED (Tim, 24 Sep, option A): the quiz offers only dishes the live Build a Bundle page lists,
   because the checkout seeds that builder and a dish outside its collections cannot be carried. Source: the
   `build-a-bundle__product-card` `data-title` sequence on https://stockedfood.com/products/build-a-bundle,
   captured 2026-09-24 with an iPhone Safari UA (34 cards: 26 in "Dishes", 8 in "Kid Friendly"), in page
   order. It is the same list the order summary sorts by (OM_BUILDER_ORDER in index.html reads it from here).
   On 24 Sep this left out four in-stock dishes: THIS Jalfrezi, Brazilian Beans, Smoky Pork and Chicken
   Cacciatore. No dish is named as an exclusion anywhere: when Stocked adds one to the builder, re-capture
   this list (`node check-builder-list.js` fetches the live page and prints any drift) and it appears in the
   quiz by itself. The gate sits in pool(), so For you, All, both kids' lists, the popup rows, the parked
   tasting and the diet counts all follow it. */
const BUILDER_LISTED = ['Chicken Tagine', 'Thai Green', 'Cape Malay Chicken', 'Jungle Pork', 'Chipotle Mushrooms', 'Miso Beans',
  'Sausage Arrabbiata', 'Harissa Beans', 'Kimchi Pork', 'Chicken Tinga', 'Saag Tempeh', 'Chicken & Chorizo', 'Chicken Panang',
  'Classic Bolognese', 'Tempeh Satay', 'Hearty Ragu', 'Marry Me Chicken', 'Curried Chickpeas', 'Beef Chilli', 'Butter Bean Puttanesca',
  'Creamy Mushrooms', 'Black Dal', 'Hot Honey Ragu', 'Tarragon Chicken', "Smoky No'rizo", 'Golden Turmeric Dal',
  'Cheeky Chicken Tikka', 'Legendary Lentil Bolognese', 'Marvellous Meaty Ragu', "Champions' Chicken Casserole", 'Super Veggie Chilli',
  'Cracking Cottage Beef', 'Creamy Coconut Curry', 'Brilliant Butternut Dal'];
const BUILDER_LISTED_CAPTURED = '2026-09-24';
const builderListed = d => BUILDER_LISTED.includes(d.n);
function pool(S, dishes, opts = {}) {
  const level = Number.isFinite(opts.heat) ? opts.heat : maxHeat(S);
  return dishes.filter(d => {
    if (d.available === false) return false;
    if (!builderListed(d)) return false;                             // in stock AND on the live builder (above)
    /* Diet (Tim, 2026-09-23): Plant-based alone is plant dishes only; Meat alone, or both, is the full
       range (a Meat table's preference is still the meat weight in score(), never a gate). The range
       has no fish. Customer copy never says "vegan".
       opts.ignoreDiet (OLE-58 rework, Tim, 28 Sep: "Plant-based needs a cross as well"): the order summary
       (menuLists) lists the whole range and lets the Filters panel's diet ticks carry the answer as a view
       filter, so a plant-based table can tick Meat. The gate still holds for everything else: the duels,
       the composed menu, keepOnMenu and the diet counts. */
    if (!opts.ignoreDiet && S.diet === 'plant' && d.p !== 'plant') return false;
    /* d.a is the product page's own label set (`allergens`), never `allergens_quiz`, which the canonical
       export leaves empty on 29 of 50 rows. The allergen screen is parked in V1, so S.allergens is empty
       unless a seed carries it; when it is asked again, this is the set it must filter on. Gluten in the
       label set can read as Barley or Wheat, so the flag matches either spelling. */
    if (S.gf && d.a.some(x => /gluten|wheat|barley|rye|oats?|spelt/i.test(x))) return false;
    if (S.allergens.some(a => d.a.some(x => x.toLowerCase() === a.toLowerCase()))) return false;
    /* opts.ignoreSpice (2026-09-23, B23): the order summary lists the whole range and lets spice SORT it
       (menuLists); the band below still gates the parked tasting and the composed menu. */
    if (!opts.ignoreSpice && !spiceBand(level, d.s)) return false;
    if (!opts.ignorePairing && S.pairing.length && !S.pairing.some(b => d.b.includes(b))) return false;   // any ranked Pairing keeps the dish
    return true;
  });
}
/* The Kids range (`k`, the eight colourful pots) is for the kids: it seats only in a kids' section, never on
   the grown-ups' table. Before 2026-09-06 the composer gave every Kids-range dish a half-point bonus on any
   table, so with no duel winners the whole range topped a solo's menu (the board's "kids meals" pins). */
const grownPool = list => list.filter(d => !d.k);
/* dishes a child would be served: kid range, and no hotter than the kids' answer */
const kidPool = (S, list) => { const k = Math.max(...kidsHeat(S)); return list.filter(d => d.k && spiceBand(k, d.s)); };
const pairingAllowed = (S, k) => !(PAIRING_ALLERGENS[k] || []).some(a => S.allergens.includes(a));
/* The Pairing chips: every Pairing with a dish behind it after the earlier filters, minus any that carries a
   declared allergen (a gluten answer never meets pasta), each with its dish count and its rank if ranked. */
function pairingOptions(S, dishes) {
  const all = pool(S, dishes, { ignorePairing: true });
  return PAIRINGS.map(([k, label]) => ({ key: k, label, count: all.filter(d => d.b.includes(k)).length, rank: S.pairing.indexOf(k) }))
    .filter(o => o.count > 0 && pairingAllowed(S, o.key));
}
const byName = (dishes, n) => dishes.find(d => d.n === n) || null;

/* ------------------------------------------------------------------- composer */
/* Diet is a weight, never a filter (issue 005): a meat table gets a meat-led menu. The composer is the one
   owner of that rule: the duels are drawn through it (meat dishes first, so a Meat table is never offered a
   plant-only pair while meat dishes remain), the winners always seat (the customer chose them), and the
   fills add at most one plant dish to a Meat table, with its reason. */
const dietWeight = (S, d) => S.diet === 'meat' ? (d.p === 'plant' ? -3 : 1.5) : 0;
/* ------------------------------------------------------- the tasting ranking (Candidate A, 2026-09-22)
   One ranking, never two scorers. The gates in pool() decide WHO is eligible; score() decides in what
   order, and pairs() decides how the eligible are shown. Every term is one column of the enriched table
   (docs/research/dish-intelligence-2026-09-22), so a weight can be argued with on the line it sits on.
   The weights are deliberately exposed: Tim will want to move them, and a weight guessed from behaviour
   is worse than a weight on a line. */
const TASTE_WEIGHTS = {
  award:     3,      // great_taste: a Great Taste win, the strongest single signal Stocked owns
  collab:    2,      // collab: a named chef or brand collaboration, hand-authored in the table
  popular:   1.2,    // reviews_count, log-scaled: 1.2 x log10(1 + n), so 527 reviews does not swamp 350
  rated:     1.5,    // rating, above the 4.2 the catalogue averages: 1.5 x max(0, rating - 4.2)
  onLevel:   1.5,    // spice exactly at the level they chose
  nearLevel: 0.5,    // spice one step BELOW the level they chose, never above (OLE-30, Tim, 27 Sep: the answer is a ceiling)
  farMilder: -0.75,  // two or more steps below, or any step above: last in line, but an award or a high rating still lifts it
  stated:    0.25,   // spice_source is the quiz's own table rather than an inference: prefer the known
  meatTable: 1.5,    // a meat preference lifts meat and pushes plant down; it is a weight, not a gate
  meatPlant: -3,
  bestSeller: 0.5    // best_seller, the catalogue's own flag
};
/* Score one dish for one Household, with the term that earned the most, so the card can say why. */
function score(S, d, opts = {}) {
  const level = Number.isFinite(opts.heat) ? opts.heat : maxHeat(S);
  const terms = [];
  const add = (id, value) => { if (value) terms.push({ id, value }); };
  /* great_taste is { stars, year } or null in the canonical export, so it is read for truthiness */
  add('award', d.great_taste ? TASTE_WEIGHTS.award : 0);
  add('collab', d.collab ? TASTE_WEIGHTS.collab : 0);
  /* reviews_count is null when the dish has no widget, which is not the same as zero reviews */
  add('popular', d.reviews_count ? TASTE_WEIGHTS.popular * Math.log10(1 + d.reviews_count) : 0);
  add('rated', d.rating ? TASTE_WEIGHTS.rated * Math.max(0, d.rating - 4.2) : 0);
  /* on the level they chose, then one step below, then two or more below or anything hotter. The last is a
     penalty, not a gate: an award (+3) or a 4.6 rating still carries a much milder dish onto the card, which is
     Tim's "go down a level for something really good". One step ABOVE earned nearLevel until OLE-30 (Tim,
     27 Sep): the spice answer means "up to this heat", so only the level below is rewarded. */
  add('heat', d.s === level ? TASTE_WEIGHTS.onLevel
            : level - d.s === 1 ? TASTE_WEIGHTS.nearLevel
            : TASTE_WEIGHTS.farMilder);
  /* the canonical export spells this "stated" (Stocked's own level) against "inferred_from_label" */
  add('stated', d.spice_source === 'stated' ? TASTE_WEIGHTS.stated : 0);
  add('bestSeller', d.best_seller ? TASTE_WEIGHTS.bestSeller : 0);
  if (S.diet === 'meat') add('meat', d.p === 'plant' ? TASTE_WEIGHTS.meatPlant : TASTE_WEIGHTS.meatTable);
  const total = terms.reduce((a, t) => a + t.value, 0);
  const top = terms.filter(t => t.value > 0).sort((a, b) => b.value - a.value)[0] || null;
  return { total, terms, top: top ? top.id : null, level,
           award: !!d.great_taste, awardYear: (d.great_taste && d.great_taste.year) || null, collab: d.collab || '', milder: d.s < level, hotter: d.s > level };
}
/* The reason line the duel card shows, from the term that won. Never a debug string: it is the sentence
   a person would say. Emoji only, no icons (design rules). */
const cap = t => t.replace(/^./, c => c.toUpperCase());
/* The words the chips use for each level, so a card never calls a medium dish mild (Tim, 2026-09-22). */
const LEVEL_WORD = { 0: 'mild', 1: 'a bit spicy', 2: 'medium', 3: 'hot' };
/* How this dish sits against the level they chose, as a clause a person would say. */
function heatClause(sc, d) {
  const gap = sc.level - d.s;
  if (gap >= 2) return 'a couple of notches milder, and worth it';
  if (gap === 1) return 'a notch milder than you said';
  if (gap < 0) return 'a little hotter';
  return `${LEVEL_WORD[sc.level] || 'spiced'} like you asked`;
}
function reasonFor(S, r) {
  const d = r.d, sc = r.s;
  const mild = sc.milder ? ` A ${heatClause(sc, d)}.` : '';
  if (sc.award) return `\u{1F3C6} Great Taste winner, ${heatClause(sc, d)}.`;
  if (sc.collab) return `\u{1F91D} A ${sc.collab} collab.`;
  if (d.reviews_count >= 100) return `\u{2B50} Loved by ${Math.floor(d.reviews_count / 50) * 50}+ customers.`;
  if (d.best_seller) return `\u{1F525} One of our best sellers.${mild}`;
  if (sc.top === 'rated' && d.rating) return `\u{2B50} Rated ${d.rating.toFixed(1)} by the people who bought it.`;
  if (sc.milder) return `\u{1F336}\u{FE0F} ${cap(heatClause(sc, d))}.`;
  if (d.s === sc.level) return `\u{1F336}\u{FE0F} ${cap(LEVEL_WORD[sc.level] || 'Spiced')} like you asked for.`;
  return `\u{1F374} Picked for your table.`;
}
/* rank(S, dishes, opts): the whole eligible pool, best first, each dish carrying its score and its
   reason. The single source of truth for "which dish is better for this Household". */
function rank(S, dishes, opts = {}) {
  const eligible = opts.list || pool(S, dishes, opts);
  return eligible
    .map(d => { const sc = score(S, d, opts); const r = { d, s: sc }; r.reason = reasonFor(S, r); return r; })
    .sort((a, b) => b.s.total - a.s.total || a.d.n.localeCompare(b.d.n));   // stable, name-ordered on a tie
}
/* pairs(S, ranked, n): n duels from a ranking.
   Round 1 seats a Great Taste dish and round 2 a collaboration, where one survives the gates; when the
   pool has none, `misses` names it rather than failing silently. Rounds 1 and 2 must differ on
   protein_group, or on sauce when the table is plant-only and the protein axis would be vacuous. No duel
   pairs two dishes of the same protein while another protein is available. */
function pairs(S, ranked, n, report) {
  const misses = [];
  const left = ranked.slice();
  const take = pred => { const i = left.findIndex(pred); return i < 0 ? null : left.splice(i, 1)[0]; };
  const plantOnly = ranked.every(r => r.d.p === 'plant');
  const axis = r => plantOnly ? r.d.r : r.d.protein_group;
  const out = [];
  /* Round 1's partner must not eat the dish round 2 is going to lead on: a pool with exactly one collab
     would otherwise seat it as a partner and then report a miss it caused itself. `spare` marks the
     dishes a later round has a claim on, and they are taken only when nothing else is left. */
  const seatRound = (leadPred, label, avoidAxis, spare, clash) => {
    const free = r => !spare || !spare(r);
    let a = leadPred ? take(r => leadPred(r) && (!avoidAxis || axis(r) !== avoidAxis)) : null;
    /* The lead we wanted exists but shares round one's axis. Seating it still beats not seating it —
       it is the round's whole job — so it goes in and the clash is reported instead of a false miss. */
    if (leadPred && !a && avoidAxis) { a = take(leadPred); if (a && clash) misses.push(clash); }
    if (leadPred && !a) { a = take(r => free(r) && (!avoidAxis || axis(r) !== avoidAxis)) || take(r => !avoidAxis || axis(r) !== avoidAxis); if (label) misses.push(label); }
    if (!a) a = take(() => true);
    if (!a) return null;
    /* the partner: a different protein while one is available, sparing a later round's lead */
    const b = take(r => free(r) && axis(r) !== axis(a)) || take(r => axis(r) !== axis(a))
           || take(free) || take(() => true);
    if (!b) { left.push(a); return null; }
    return [a, b];
  };
  const r1 = seatRound(r => r.s.award, 'no Great Taste dish survived the gates for round one', null, r => !!r.s.collab);
  if (r1) out.push(r1);
  const r2 = seatRound(r => !!r.s.collab, 'no collaboration dish survived the gates for round two',
    r1 ? axis(r1[0]) : null, null,
    'the only collaboration left shares round one\'s ' + (plantOnly ? 'sauce' : 'protein') + ', so the rounds do not vary there');
  if (r2) out.push(r2);
  while (out.length < n) {
    const a = take(() => true); if (!a) break;
    const b = take(r => axis(r) !== axis(a)) || take(() => true);
    if (!b) { left.push(a); break; }
    out.push([a, b]);
  }
  /* THE FLOOR, kept from the 2026-09-22 one-tap incident: a pool too small to give every round two fresh
     dishes still fills the tasting, by forming pairs that re-use dishes already shown, never twice in one
     pair. A spice band can cut the pool to five dishes, which is two fresh pairs and four possible ones.
     Fewer than n comes back only when the pool cannot make n DISTINCT pairs at all. */
  if (out.length < n && ranked.length >= 2) {
    const seen = new Set(out.map(([a, b]) => a.d.n + '|' + b.d.n));
    for (let i = 0; out.length < n && i < ranked.length; i++)
      for (let j = i + 1; out.length < n && j < ranked.length; j++) {
        const key = ranked[i].d.n + '|' + ranked[j].d.n;
        if (seen.has(key)) continue;
        seen.add(key); out.push([ranked[i], ranked[j]]);
      }
  }
  if (report) { report.misses = misses; }
  return out.slice(0, n);
}

/* The tasting is DUEL_ROUNDS rounds. The number is a contract, not a nicety: index.html's pickDuel treats
   the last answered pair as the end of the tasting, so a set that came back short used to send the customer
   to the order summary after a single tap (2026-09-22 incident, see duelPairs below). */
const DUEL_ROUNDS = 4;
/* Pairs from the pool, in three passes, each a weaker guarantee than the last:
     1. the five designed contrasts (creamy against tomato, and so on), every dish fresh;
     2. fresh dishes in catalogue order, while any are left;
     3. THE FLOOR (added 2026-09-22): pairs that re-use dishes already shown, never twice in one pair, so a
        pool too small to give every round two fresh dishes still fills the tasting.
   It returns fewer than n only when the pool cannot make n DISTINCT pairs at all, which needs a pool of
   three dishes or fewer; the caller is then responsible for a tasting that is honestly shorter, or for not
   offering one. Root cause of the incident: pool() still applies the PARKED Pairing and Allergen filters,
   so a state carrying `pairing: ['wrap']` (a preset, a board seed, a ?state= link) left two grown-up dishes
   and pass 2 broke out with one pair. */
function duelPairs(S, p, n) {
  const axes = [['creamy', 'tomato'], ['tomato', 'creamy'], ['creamy', 'dry'], ['tomato', 'broth'], ['creamy', 'tomato']];
  const ranked = p.slice().sort((a, b) => dietWeight(S, b) - dietWeight(S, a));   // stable: catalogue order within a weight
  const out = [], used = new Set();
  const free = () => ranked.filter(d => !used.has(d.n));
  for (const [x, y] of axes) {
    const a = free().find(d => d.r === x);
    const b = free().find(d => d.r === y && d.n !== (a && a.n));
    if (a && b) { out.push([a, b]); used.add(a.n); used.add(b.n); }
    if (out.length === n) return out;
  }
  while (out.length < n) { const rest = free(); if (rest.length < 2) break; out.push([rest[0], rest[1]]); used.add(rest[0].n); used.add(rest[1].n); }
  if (out.length < n && ranked.length >= 2) {
    const seen = new Set(out.map(([a, b]) => a.n + '|' + b.n));
    for (let i = 0; out.length < n && i < ranked.length; i++)
      for (let j = i + 1; out.length < n && j < ranked.length; j++) {
        const key = ranked[i].n + '|' + ranked[j].n;
        if (seen.has(key)) continue;
        seen.add(key); out.push([ranked[i], ranked[j]]);
      }
  }
  return out;
}
/* duelSets(S, dishes): the tasting, from the ranking.

   THE KIDS RULE, read before any ranking and never a weight (Tim, 2026-09-22, deterministic):
     same menu      the Kids range is excluded outright and there is ONE tasting, DUEL_ROUNDS rounds.
     separate       TWO tastings. The grown-ups' comes from the non-kids pool, the kids' from the Kids
                    range at the kids' own spice ceiling. Each is sized to the dishes its own Box section
                    needs (boxPlan's tablePacks / kidPacks at two Packs a dish, ADR 0003), shows twice as
                    many options as picks, and takes that many picks: two grown-ups' picks is four options
                    across two duels, then the same for the kids.
   The kids' spice answer applies to the kids' tasting only. */
function duelSets(S, dishes) {
  const report = {};
  const H = household(S);
  const grown = () => rank(S, dishes, { list: grownPool(pool(S, dishes)) });
  if (!H.splitKids) {
    const set = { adult: pairs(S, grown(), DUEL_ROUNDS, report), kids: [], misses: report.misses || [] };
    return set;
  }
  /* Each section takes as many picks as its share of the Box needs at TWO Packs a dish (ADR 0003's
     shape rule, which is also the arity rule here): four grown-up Packs is two picks, shown as two duels
     of two options. Bounded at one, and at DUEL_ROUNDS so a big Box does not become a long tasting. */
  const plan = boxPlan(S);
  const bound = n => Math.max(1, Math.min(DUEL_ROUNDS, Math.ceil((n || 4) / 2)));
  const adultPicks = bound(plan && plan.tablePacks);
  const kidPicks = bound(plan && plan.kidPacks);
  const kidHeat = Math.max(...kidsHeat(S));
  const kidsEligible = pool(S, dishes, { heat: kidHeat }).filter(d => d.k);
  const adult = pairs(S, grown(), adultPicks, report);
  const adultMisses = (report.misses || []).map(m => "the grown-ups' tasting: " + m);
  const kids = pairs(S, rank(S, dishes, { list: kidsEligible, heat: kidHeat }), kidPicks, report);
  const kidMisses = (report.misses || []).map(m => "the kids' tasting: " + m);
  return { adult, kids, misses: [...adultMisses, ...kidMisses] };
}
/* How many rounds this state's tasting can honestly run. Zero means there is nothing to taste and the
   screen must not be offered at all: rendering it with no pairs threw on `pairs[i]`. */
const duelRounds = (S, dishes) => { const d = duelSets(S, dishes); return d.adult.length + d.kids.length; };
/* The menu IS what they picked: every duel winner lands on it, in the section it was won in; the scorer
   fills only the slots a "neither, honestly" left empty and chooses the recommendations. */
function composeMenu(S, dishes) {
  const p = pool(S, dishes), H = household(S);
  const winners = arr => arr.map(n => n && byName(p, n)).filter(Boolean);
  /* ONE ranking, never two scorers (Tim, 2026-09-22): the slots the winners do not fill are filled from
     rank()'s order, best first, and each filled dish keeps the reason that put it there so the summary
     can mark the row "picked for you". The old local scorer — a sauce echo of the duel winners plus the
     diet weight — is gone; the sauce echo now lives in the ranking's own terms. */
  const order = new Map(rank(S, dishes).map((r, i) => [r.d.n, { at: i, reason: r.reason }]));
  const rankOf = d => (order.has(d.n) ? order.get(d.n).at : Number.MAX_SAFE_INTEGER);
  const seen = new Set(), reasons = {}, pickedForYou = {};
  let plantSeated = 0;
  const seat = (arr, d, cap, why) => {
    if (!d || arr.length >= cap || seen.has(d.n)) return;
    if (why === 'fill' && S.diet === 'meat' && d.p === 'plant') {           // the fills add at most one plant dish to a Meat table
      if (plantSeated >= 1) return;
      reasons[d.n] = 'The one plant dish on a meat table: it beat the meat dishes on the heat and the plate you told us about.';
    }
    if (d.p === 'plant') plantSeated++;
    if (why === 'fill' && order.has(d.n)) pickedForYou[d.n] = order.get(d.n).reason;
    arr.push(d); seen.add(d.n);
  };
  const fill = (arr, list, cap) => list.filter(d => !seen.has(d.n)).sort((a, b) => rankOf(a) - rankOf(b)).forEach(d => seat(arr, d, cap, 'fill'));
  const table = [], forKids = [];
  /* How many dishes the menu seats is now the box's own arithmetic: enough to hold the packs the plan
     bought, at the section's packs a dish. Before 2026-09-22 this was a flat four, or BIG_TABLE_DISHES on
     a big table, which could not fill an eight-pack box. */
  /* Before the Intention is answered there is no Box size, so the menu seats the old flat four (and three
     on a big table): enough to render a menu, never enough to imply a Box. */
  const plan = boxPlan(S) || { dishes: { table: sectionsBig(S).table ? BIG_TABLE_DISHES : 4, kids: 2 } };
  if (H.splitKids) {
    winners(S.kidDuels).forEach(w => seat(forKids, w, plan.dishes.kids, 'won'));
    fill(forKids, kidPool(S, p), plan.dishes.kids);
    winners(S.duels).forEach(w => seat(table, w, plan.dishes.table, 'won'));
    fill(table, grownPool(p), plan.dishes.table);
  } else {
    winners(S.duels).forEach(w => seat(table, w, plan.dishes.table, 'won'));
    fill(table, grownPool(p), plan.dishes.table);
  }
  /* Runners-up follow the shape of the menu: two for the table then two for the kids on a split table,
     otherwise the top four left. */
  const rest = p.filter(d => !seen.has(d.n)).sort((a, b) => rankOf(a) - rankOf(b));
  const spare = H.splitKids ? [...grownPool(rest).slice(0, 2), ...kidPool(S, rest).slice(0, 2)] : grownPool(rest).slice(0, 4);
  return { table, forKids, spare, poolSize: p.length, reasons, pickedForYou };
}
/* Which packs are the kids' packs: the kids' menu picks, plus any kid-friendly dish added from the
   recommendations. The list, the maths and the plan agree on this because they all ask here. */
const kidPackNames = menu => new Set([...menu.forKids.map(d => d.n), ...menu.spare.filter(d => d.k).map(d => d.n)]);

/* menuLists(S, dishes): what the order summary lists to pick from (2026-09-23; the duels are parked, so
   picking happens here). Every eligible dish, in the section it belongs to.
   THE KIDS RULE, unchanged from the tasting: no kids, or kids who eat the same, and the Kids range is
   hidden; kids who eat separately get their own list, which is the Kids range.
   SPICE SORTS, NEVER FILTERS (Tim, B23, 2026-09-23: "the order summary still shows the full range"). Diet,
   stock and allergens still gate; spice only orders. Each list is the dishes near the section's level
   first ("For you"), then the rest of the range, best first.
   THE SPICE ANSWER IS A CEILING (OLE-30, Tim, 27 Sep, from Sam Moss's "If I say I like medium spicy, I should
   see everything below that", 25 Sep). "Near" is the chosen level and every level below it, never above:
   the chosen level first, then each lower level in turn, best first by the one ranking inside a level.
   Before, "near" was one level either side, with "not spicy" alone a ceiling at 0 (Tim, 2026-09-22); now every
   level works as "not spicy" did. B23 still holds for All: the hotter dishes follow in the rest of the range,
   so nothing is hidden, and the Filters panel (C14) stays as picked.
   The grown-ups' level is the table's (maxHeat); the kids' list is sorted at the kids' own answer, by the same
   rule.
   DIET IS A VIEW FILTER HERE TOO (OLE-58 rework, Tim, 28 Sep: "Plant-based needs a cross as well"). The lists
   hold the whole range whatever the diet answer; the answer arrives as the Filters panel's diet ticks
   (dietTicks, menuFilter), so a plant-based table opens on its plant dishes and can tick Meat. Within a type
   the order is unchanged: the ranking is per dish. opts.dietGate restores the hard gate for keepOnMenu, so a
   switch to Plant-based still drops the meat packs from the Box as before. */
const nearLevel = (level, s) => s <= level;
function menuLists(S, dishes, opts = {}) {
  const H = household(S), ignoreDiet = !opts.dietGate;
  const sorted = (list, level) => {
    const ranked = rank(S, dishes, { list, heat: level }).map(r => r.d);
    /* the chosen level first, then each level below (sort is stable, so the ranking holds inside a level) */
    const near = ranked.filter(d => nearLevel(level, d.s)).sort((a, b) => b.s - a.s);
    return { all: [...near, ...ranked.filter(d => !nearLevel(level, d.s))], near };
  };
  const level = maxHeat(S);
  const t = sorted(grownPool(pool(S, dishes, { ignoreSpice: true, ignoreDiet })), level);
  /* kidsRange: the whole Kids range the gates allow, sorted at the kids' level, whatever the table. The
     order summary's "All" view shows it as the Kids' menu when the table has kids (B25), even when they eat
     the same; `kids` stays the split table's own list. */
  const kidLevel = Math.max(...kidsHeat(S));   // a shared table's Kids' menu sorts at the one answer
  const k = sorted(pool(S, dishes, { ignoreSpice: true, ignoreDiet, heat: kidLevel }).filter(d => d.k), kidLevel);
  const out = { table: t.all, forYou: t.near, level, kids: [], kidsForYou: [], kidLevel: null, kidsRange: k.all };
  if (!H.splitKids) return out;
  return { ...out, kids: k.all, kidsForYou: k.near, kidLevel };
}

/* ----------------------------------------------------------- a tap on the menu (2026-09-23, QZ-10) */
/* What a tap does to the Box. A tap is one pack (packsPerTap, 2026-09-23). The biggest Box
   on sale is liveCeiling() (8). What happens when a tap would pass it is Tim's open question, so the answer
   is a switch, set to the recommended option:
     'fit'     (B, recommended) the tap adds only what still fits and the bar says "Your box is full at 8
               packs". The Box can never pass the ceiling, so checkout is never blocked by the quiz itself.
     'refuse'  (A) a tap that does not fit whole adds nothing; the same message shows.
     'allow'   (C, the behaviour before QZ-10) the tap always adds; a Box over the ceiling is trimmed by the
               notice above the list before checkout.
   Switch it here, or per call with opts.mode (the tests do). */
const OVERFILL_MODES = ['fit', 'refuse', 'allow'];
const BOX_OVERFILL = 'fit';
const packsIn = box => Object.values(box || {}).reduce((a, b) => a + b, 0);
/* tapBox(S, box, name, section, dir): a pure step. dir +1 adds a tap's worth, -1 takes one away (never
   below zero, and a dish the fill left short leaves in one tap). Returns the new Box, what moved, the step,
   and `full` when a + could not add its whole step. The Box passed in is never mutated. */
function tapBox(S, box, name, section, dir, opts = {}) {
  const mode = OVERFILL_MODES.includes(opts.mode) ? opts.mode : BOX_OVERFILL;
  const step = packsPerTap(S, section), ceiling = liveCeiling();
  const next = { ...(box || {}) }, have = next[name] || 0;
  if (dir < 0) {
    const q = Math.max(0, have - step);
    if (q) next[name] = q; else delete next[name];
    return { box: next, added: q - have, step, full: false };
  }
  const room = Math.max(0, ceiling - packsIn(box));
  const add = mode === 'allow' || step <= room ? step : mode === 'fit' ? room : 0;
  if (add > 0) next[name] = have + add;
  return { box: next, added: add, step, full: mode !== 'allow' && add < step };
}
/* boxBar(S, packs): the sticky Box bar under the menu, from the answers and the Packs held. The end of
   the track is the TARGET the answers set (frequencyPlan's packs; the ceiling before the Intention is
   answered). Continue needs Stocked's Minimum (4 Packs, the live builder's "Add 1 more pack to start your
   box"); nothing else stops it, so a full Box always continues to Frequency.
   The customer word is "box", the live builder's ("to start your box"): OLE-71, Tim 29 Sep, reversing OLE-60's
   "basket" ("minimal changes and keep the language consistent"). Over the ceiling still reads "in one delivery"
   (OLE-60's wording, kept). The button reads "Order · N packs", the shape of the old "Box · N packs" (Tim, OLE-71 29 Sep,
   after checking the Grab pattern: "first one says order and then X number of packs and then it says add to
   order"); the Frequency CTA then reads "Add to order" (index.html). */
function boxBar(S, packs, opts = {}) {
  const mode = OVERFILL_MODES.includes(opts.mode) ? opts.mode : BOX_OVERFILL;
  const plan = frequencyPlan(S);
  const ceiling = liveCeiling(), target = plan ? plan.packs : ceiling;
  const n = x => `${x} pack${x === 1 ? '' : 's'}`;
  const full = mode !== 'allow' && packs >= ceiling;
  const label = packs ? `Order · ${n(packs)}` : 'Tap + to start your box';   // Tim, 2026-09-24: the packs held, never "3 of 4"; the price sits beside it on the page
  const line = !packs ? 'Tap + on a dish to start your box.'
             : packs < MIN_PACKS ? `Add ${NUM[MIN_PACKS - packs] || MIN_PACKS - packs} more pack${MIN_PACKS - packs === 1 ? '' : 's'} to start your box.`
             : full ? `Your box is full at ${ceiling} packs.`
             : packs < target ? `${cap(NUM[target - packs] || String(target - packs))} more pack${target - packs === 1 ? '' : 's'} to fill your box.`
             : packs > ceiling ? `${packs} packs is more than we can send in one delivery.`
             : `Your box is filled. Room for ${NUM[ceiling - packs] || ceiling - packs} more.`;
  return { packs, target, ceiling, fill: Math.min(1, packs / target), label, line, full,
           canContinue: packs >= MIN_PACKS && (mode !== 'allow' || packs <= ceiling) };
}
/* milestoneFor(packs): the sticky panel's message row and its track (Tim, 2026-09-24, grilled and "go"),
   read off MILESTONES. The row names the NEXT Milestone and the packs still to add, counting live:
     0      📦 Add **4 packs** to start your box
     1-3    📦 Add **N more pack(s)** to start your box
     4-5    🚚 Add **N more pack(s)** to get free delivery
     6-7    🏷️ Add **N more pack(s)** to get an extra 10% off
     8+     🎉 **Free delivery and an extra 10% off.**
   `bold` is plain text the caller wraps in <b>; `before` and `after` sit either side of it. The track fills
   packs over the last Milestone (8), never resets and carries no marks.
   OLEO-197 (the 9 Oct replays): the row never invites a pack the Box cannot take. It names a next Milestone only
   while the Box is under liveCeiling(), so whenever it says "Add N more", a + adds (tapBox). With today's numbers
   the last Milestone IS the ceiling (8 and 8) and nothing changes. If the ceiling ever drops below a Milestone, that
   Milestone is skipped: under the ceiling the row invites only what still fits ("Add 1 more pack to fill your box",
   boxBar's words), and at the ceiling it says the Box is full. */
const FULL_BOX_NOTE = 'Your box is full';   // OLEO-197: a + tapped on a full Box (Quiz V2); Tim 9 Oct (comment 763805a9): just "Your box is full"
function milestoneFor(packs) {
  const steps = MILESTONES.filter(m => m.packs), last = steps[steps.length - 1].packs, ceiling = liveCeiling();
  const n = Math.max(0, packs | 0), next = steps.find(m => n < m.packs && m.packs <= ceiling), fill = Math.min(1, n / last);
  if (!next && n < last) {
    const box = milestone('start').emoji, room = ceiling - n;
    if (room > 0) { const bold = `${room} more pack${room === 1 ? '' : 's'}`; return { id: 'fill', emoji: box, before: 'Add ', bold, after: ' to fill your box', text: `Add ${bold} to fill your box`, left: room, fill, done: false }; }
    const t = `Your box is full at ${ceiling} packs.`; return { id: 'full', emoji: box, before: '', bold: t, after: '', text: t, left: 0, fill, done: true };
  }
  if (!next) { const d = milestone('done'); return { id: d.id, emoji: d.emoji, before: '', bold: d.text, after: '', text: d.text, left: 0, fill, done: true }; }
  const left = next.packs - n, bold = n ? `${left} more pack${left === 1 ? '' : 's'}` : `${next.packs} packs`;
  return { id: next.id, emoji: next.emoji, before: 'Add ', bold, after: ` ${next.goal}`, text: `Add ${bold} ${next.goal}`, left, fill, done: false };
}
/* sheetHistory(h, on): the dish sheet's own history entry (OLEO-197, Quiz V2; replay 01a1168f: the phone's Back, meant
   to close the dish details, stepped the quiz back to the diet or spice screen and left the sheet open over it).
   h is window.history (or a test's fake: state, pushState, back). lp-quiz-dish-popup.js calls hold() as the sheet
   opens: one entry is pushed on top of the screen's, a copy of its state with `sheet: true`, so the hash and the
   screen stay. lp-quiz.js's popstate handler asks popped(state) first:
     { close: true, swallow: true }    the Back that left the sheet's entry: close the sheet, the screen stays
     { close: true, swallow: false }   a Back past it (a long-press jump): close the sheet, the quiz renders as before
     { close: false, swallow: true }   the history.back() release() made itself: nothing more to do
     { close: false, swallow: false }  not the sheet's: the quiz's own Back and Forward, unchanged
   release() is the sheet closing by its own control (×, Done, the backdrop, a swipe, Escape): it takes its entry off
   with history.back(), so the history stays clean. A stale sheet entry (Forward after a Back closed the sheet) is
   reused, never stacked. `on` false (today's quiz, an embedded board, ?force=) changes nothing. */
function sheetHistory(h, on) {
  let held = null, expect = null;
  return {
    hold() {
      if (!on || held) return false;
      const st = h.state; if (!st || !st.quiz) return false;
      if (!st.sheet) h.pushState(Object.assign({}, st, { sheet: true }), '');
      held = st.quiz; return true;
    },
    release() {
      if (!held) return false;
      const key = held; held = null;
      if (!(h.state && h.state.sheet)) return false;
      expect = key; h.back(); return true;
    },
    popped(state) {
      const own = state && !state.sheet ? state.quiz : null;
      if (expect) { const k = expect; expect = null; if (own === k) return { close: false, swallow: true }; }
      if (held) { const k = held; held = null; return { close: true, swallow: own === k }; }
      return { close: false, swallow: false };
    },
    get held() { return held; }
  };
}
/* FOR YOU: the order summary opens on a short list of EIGHT dishes per menu. A table that eats the same
   menu sees eight; a table whose kids eat their own menu sees eight for the grown-ups and eight for the kids
   (OLE-79, Tim, 29 Sep: "we need to times them by two. So the adults get eight and the kids get eight too").
   Replaces the split of the one eight into four and four (Tim, 23 Sep), which itself replaced "twice what the
   table needs". The count is a ceiling: when fewer dishes qualify, For you shows only those (forYouList).
   OLEO-155 (Tim, 5 Oct: "for you, we double the recommendations and have the filters active"): SIXTEEN per menu
   now, the grown-ups' and a split table's kids' alike. The Kids range has eight dishes, so a kids' For you still
   shows at most eight. */
const FOR_YOU_DISHES = 16, FOR_YOU_DISHES_LIVE = 8;   // OLEO-154: sixteen on V2A and V2B; today's quiz keeps the live eight
function forYouCount(S, section = 'table', variant = VARIANT) {
  return v11(variant) ? FOR_YOU_DISHES : FOR_YOU_DISHES_LIVE;
}
/* The For you list itself: only dishes at or below the level they chose (OLE-30, Tim, 27 Sep: a ceiling,
   never a hotter dish), the chosen level first and then each lower level, then a Great Taste award or a named
   collab, then the one ranking. When fewer dishes qualify than forYouCount, For you shows only those. Never
   outside menuLists, so the diet gate and the Kids rule hold here too; the kids' For you applies the same
   ceiling at the kids' level. */
/* `keep` (optional) is a view filter, e.g. the order summary's diet ticks (menuFilter().adults). It filters BEFORE
   the count, so a filtered For you still shows up to its count, from the dishes it allows under the ceiling. */
/* `heat` (optional, OLE-30 rework, Tim 27 Sep) is the heat levels ticked in the Filters panel, and it REPLACES
   the answer's ceiling for this list. On arrival the ticks are the answer's own (heatTicks), so For you is the
   ceiling list above. Unticking a level drops its dishes; ticking a level above the answer adds its dishes AFTER
   every level at or below the answer, nearest first, so the OLE-30 order (the chosen level first, then each
   lower level) still leads. The kids' list never takes it: the panel's heat ticks are the grown-ups'. */
function forYouList(S, dishes, section = 'table', keep = null, heat = null, variant = VARIANT) {
  const L = menuLists(S, dishes);
  const all = section === 'kids' ? L.kids : L.table, level = section === 'kids' ? L.kidLevel : L.level;
  const allowed = section !== 'kids' && Array.isArray(heat) ? d => heat.includes(d.s) : d => nearLevel(level, d.s);
  const list = (keep ? all.filter(keep) : all).filter(allowed);
  const tier = d => d.s <= level ? level - d.s : d.s;   // at or below: 0 = the chosen level; above: after every lower level
  const star = d => d.great_taste || d.collab ? 0 : 1;
  const at = new Map(list.map((d, i) => [d.n, i]));
  const ranked = list.slice().sort((a, b) => tier(a) - tier(b) || star(a) - star(b) || at.get(a.n) - at.get(b.n));
  const both = v11(variant) === 'a' && section !== 'kids' && S.diet === 'mix';   // OLEO-176: Quiz V2A only
  return (both ? alternateDiets(ranked) : ranked).slice(0, forYouCount(S, section, variant));
}
/* OLEO-176 (Weekly Growth Meeting, 7 Oct: "I selected plant-based and meat, but it was only meat that came up"; Tim,
   grill Q12 B). On V2A a "both" table (diet 'mix') sees For you positions 1-8 alternate meat and plant. The ranked
   list above is split into its meat and plant halves, each in that same order (spice tier, Great Taste or a collab,
   the ranking); the half of the first ranked dish leads, so position 1 never changes. A half that runs out is
   filled from the other. After position 8 the rest is the ranked list with the placed dishes removed. Every other
   diet answer, the kids' list, V2B and today's quiz keep the ranked order exactly. */
const FOR_YOU_ALTERNATE = 8;
function alternateDiets(ranked, n = FOR_YOU_ALTERNATE) {
  if (!ranked.length) return ranked;
  const half = { meat: ranked.filter(d => dietOf(d) === 'meat'), plant: ranked.filter(d => dietOf(d) === 'plant') };
  const lead = dietOf(ranked[0]), other = lead === 'meat' ? 'plant' : 'meat', top = [];
  for (let i = 0; top.length < n && (half.meat.length || half.plant.length); i++) {
    const want = i % 2 ? other : lead;
    top.push((half[want].length ? half[want] : half[want === 'meat' ? 'plant' : 'meat']).shift());
  }
  return top.concat(ranked.filter(d => !top.includes(d)));
}
/* THE MENU FILTERS (C14, Charlie; Tim picked the Filters panel, variant C, on 25 Sep). The order summary's
   Filters panel holds two sets of inclusion ticks, and this is what they mean, so For you, All and the tests
   read one rule. Ticked means shown, in both sets:
     diet     the diet types ticked, one tick each, worded as the diet question's own answers ('meat' Meat,
              'plant' Plant-based). A dish shows when its type is ticked: d.p === 'plant' is Plant-based,
              anything else is Meat (dietOf).
     heat     the heat levels ticked, one tick per level, worded as the spice question's own answers (0 Mild,
              1 A bit spicy, 2 Medium spicy, 3 Spicy!). A dish shows when its heat is ticked.
   THE HEAT TICKS CARRY THE ANSWER (OLE-30 rework, Tim, 27 Sep: "if someone clicked medium spicy, then in the spicy
   filter in the order summary, what's ticked is Mild, a bit spicy, and medium spicy, and then obviously very
   spicy is unticked", and the wording is "exactly the question"). On arrival the ticks are heatTicks(S): the
   answer as a ceiling, every level from Mild up to it. This replaced C14's single "and up" choice ("A bit spicy
   and up", "Medium and up", "Spicy! only", Tim 25 Sep). No tick, or all four, is no heat filter (every heat), as
   on the live builder where nothing ticked filters nothing.
   THE DIET TICKS CARRY THE DIET ANSWER (OLE-58, Tim, 28 Sep, on the live quiz: "Plant-based always needs to be
   pre-selected unless they select meat only, because I selected both and now it was unselected, so I couldn't
   see the plant-based options"). Until then the Plant-based tick meant plant dishes ONLY (the old chip's meaning),
   so a Both table opened with it unticked and the one panel read two ways. Now it is an inclusion tick beside a
   Meat tick. On arrival the ticks are dietTicks(S): Both → both ticked; Plant-based → Plant-based only; Meat →
   Meat only, so a Meat table opens with its plant dishes hidden until Plant-based is ticked (Tim accepted that;
   the rank and duel rules are unchanged). No diet tick, or both, is no diet filter, the same convention as heat.
   THE DIET ANSWER SETS THE ARRIVAL TICKS AND NOTHING ELSE (OLE-58 rework, Tim, 28 Sep: "Plant-based needs a
   cross as well, no?"): the ticks work exactly like the heat ticks, so a plant-based table can tick Meat and
   see the meat dishes, or untick Plant-based and see meat only, and its Plant-based pill has its cross like
   every other. menuLists lists the whole range for that (its diet gate is opts.dietGate now, kept for
   keepOnMenu); the duels, the composed menu and the diet counts keep the gate.
   `adults` narrows the adults' dishes; `kids` narrows the Kids range. Heat never filters the kids' dishes (as
   under C14): the Kids range is all Not spicy and a split table's kids have their own answer, which their own
   For you keeps as its ceiling; the diet ticks do filter them. `levels` is what For you takes as its heat
   (forYouList's `heat`): the ticks, or all four when there is no heat filter. `active` is the Filters badge:
   1 per ticked diet while the diet filter is on, 1 per ticked heat while the heat filter is on (the page shows
   pills for the heat ticks only; the diet ticks live in the panel alone, Tim 28 Sep). Both are view filters,
   applied before For you's count; transient, never in S and never in the order record. */
const HEAT_LEVELS = [0, 1, 2, 3];
const DIET_TYPES = ['meat', 'plant'];
const heatTicks = S => HEAT_LEVELS.filter(n => n <= maxHeat(S));   // the ceiling the answer sets, as ticks (For you's level)
const dietTicks = S => S.diet === 'plant' ? ['plant'] : S.diet === 'meat' ? ['meat'] : DIET_TYPES.slice();   // Both (or unanswered) is every diet
const dietOf = d => d.p === 'plant' ? 'plant' : 'meat';
function menuFilter(f = {}) {
  const dietTicked = DIET_TYPES.filter(t => asArray(f.diet).includes(t));
  const diet = dietTicked.length && dietTicked.length < DIET_TYPES.length ? dietTicked : null;   // null = every diet
  const ticked = HEAT_LEVELS.filter(n => asArray(f.heat).some(h => h != null && h !== '' && int(h, -1) === n));
  const heat = ticked.length && ticked.length < HEAT_LEVELS.length ? ticked : null;   // null = every heat
  const dietOk = d => !diet || diet.includes(dietOf(d));
  return { diet, dietTicked, heat, ticked, levels: heat || HEAT_LEVELS.slice(), active: (diet ? diet.length : 0) + (heat ? heat.length : 0),
           adults: d => dietOk(d) && (!heat || heat.includes(d.s)), kids: dietOk };
}
/* tapLine ("Each + adds 2 packs: a meal for all four of you.") was removed on 2026-09-23 with the
   packs-a-tap rule: a + is one pack, and the page says only "1 pack feeds 2 people". */

/* ---------------------------------------------------------------------- quote */
/* Which sections need more than one Pack for a Dinner: those open at two Packs a dish.
   OLE-68: the Box's SHAPE stays at V1_PLATE for everyone, like the target and packsPerDish (ceil(people / 2),
   one pack for two people), so 2 Blocks a kid moves no dish count; only the meals counted change. */
function sectionsBig(S) {
  const H = household(S);
  const adultDemand = H.adults * V1_PLATE + (H.splitKids ? 0 : H.kids * V1_PLATE);
  return { table: adultDemand > BLOCKS_PER_PACK, kids: H.splitKids && H.kids * V1_PLATE > BLOCKS_PER_PACK };
}
/* -------------------------------------------------------------- the box, built to the answer */
/* V1, second rule change (Tim, 2026-09-22). The Box is no longer opened at a flat Minimum and grown by the
   customer: it is BUILT to the meals answer, in the client's own words —
     "A plate is 4 Blocks for everyone. We stock four weeks: people x 4 Blocks x meals a week x 4 weeks,
      divided by 8 Blocks a pack, rounded up to whole packs, never fewer than 4 packs and never more than 8."
   The two bounds are the old ones: four packs is Stocked's real minimum order (MIN_PACKS, ADR 0003) and
   eight is the biggest box on sale (NUDGE_CAP_PACKS; twelve may follow). Because 4 Blocks x 4 weeks is
   exactly 2 packs a person a meal, the arithmetic before the bounds is people x meals x 2, which is why
   `wanted` below is an integer and the cap is easy to see coming.
   ADR 0003 still decides the SHAPE of the box: a section whose Dinner is bigger than a Pack takes two
   packs of each dish so a Dinner never comes from two dishes, so eight packs on a family table is four
   dishes at two each, and eight packs for a couple is eight dishes at one each. */
/* boxPlan(S): the SHAPE of the Box, and only the shape. How the Packs divide between the table and the
   kids, and how many dishes each section carries. It no longer decides HOW MANY Packs — frequencyPlan
   owns that, and this reads it, so the two can never disagree (the cancel-out this redesign removes was
   exactly two functions deciding the size from the same answer twice).
   ADR 0003 still decides the shape: a section whose Dinner is bigger than a Pack takes two Packs of each
   dish, so every Dinner comes from one dish.
   Returns null when the plan is null, i.e. before the Intention is answered. */
function boxPlan(S) {
  const plan = frequencyPlan(S);
  if (!plan) return null;
  const H = household(S), packs = plan.packs;
  /* Packs a dish = packs a tap (2026-09-23): ceil(people / 2), so a dish always feeds its section's whole
     table at one meal. Up to four at the table this is ADR 0003's one-or-two; at five or six it is three,
     which is what closes B29 (two Packs, 16 Blocks, could not feed five at 20). */
  const perDish = { table: packsPerDish(S, 'table'), kids: packsPerDish(S, 'kids') };
  /* How the packs divide between the two sections of a split table: by the people in each, never below
     one pack, and the grown-ups carry the rounding. A table on one menu is all one section. */
  let kidPacks = 0;
  if (H.splitKids) kidPacks = Math.min(packs - perDish.table, Math.max(perDish.kids, Math.round(packs * H.kids / H.people)));
  const tablePacks = packs - kidPacks;
  const dishes = { table: Math.max(1, Math.ceil(tablePacks / perDish.table)),
                   kids: H.splitKids ? Math.max(1, Math.ceil(kidPacks / perDish.kids)) : 0 };
  return { packs, perDish, tablePacks, kidPacks, dishes };
}
/* The Box the quiz builds: the plan's packs, laid over the menu's dishes at the section's packs a dish, and
   any remainder spread a pack at a time so the total is exactly what the plan asked for. */
function fillSection(box, list, packs, perDish) {
  if (!list.length || packs <= 0) return;
  let left = packs;
  list.forEach(d => { const q = Math.min(perDish, left); if (q > 0) { box[d.n] = q; left -= q; } });
  for (let i = 0; left > 0 && list.length; i = (i + 1) % list.length) { box[list[i].n] = (box[list[i].n] || 0) + 1; left--; }
}
function openBox(S, dishes, menu) {
  menu = menu || composeMenu(S, dishes);
  const plan = boxPlan(S);
  if (!plan) return {};                    // no Intention, no Box: the sheet renders empty rather than guessing
  const box = {};
  fillSection(box, menu.table, plan.tablePacks, plan.perDish.table);
  fillSection(box, menu.forKids, plan.kidPacks, plan.perDish.kids);
  return box;
}
/* What the Box opens at for this Household: the built size, not a flat four. */
const minimumFor = (S, dishes, menu) => Object.values(openBox(S, dishes, menu)).reduce((a, b) => a + b, 0);
/* A section is a group of people who eat the same packs (the whole table on one menu; the grown-ups and the
   kids apart when the kids have their own picks), each row with its own demand per night in Blocks:
   grown-ups at the Plate of the row's Pairing, kids at theirs. */
/* Whole Dinners come from one dish, each row at its own Pairing's Plate; the Spare assembles the rest at the
   section's demand. Pooling across dishes for a Dinner bigger than a Pack was removed 2026-09-05: a Dinner is
   never two grown-up dishes unless it is labelled as built from what is left. */
const wholeNights = x => x.rows.reduce((a, r) => a + Math.floor(r.q * BLOCKS_PER_PACK / r.demand), 0);
const spareBlocks = x => x.rows.reduce((a, r) => a + (r.q * BLOCKS_PER_PACK) % r.demand, 0);
const secNights = x => wholeNights(x) + Math.floor(spareBlocks(x) / x.demand);
const nightsOf = secs => secs.length ? Math.min(...secs.map(secNights)) : 0;
function sections(S, dishes, box, menu, pairingOf) {
  const H = household(S), D = dinnerFor(S);
  const rows = Object.entries(box).filter(([, q]) => q > 0).map(([n, q]) => ({ d: byName(dishes, n), q })).filter(r => r.d);
  /* A section's own demand, for pooling and for the Dinners the Spare assembles, is the Dinner at the
     section's Plate: a Dinner built from what is left is never counted at a cheaper Plate than the one
     the Household asked for. */
  const sectionDemand = kidsRow => kidsRow ? H.kids * D.kidPlate : H.adults * D.adultPlate + (H.splitKids ? 0 : H.kids * D.kidPlate);
  /* V1: every row is served at the same Plate, so a row's demand IS its section's demand and no row can be
     "stretched" onto a cheaper Pairing. Before the V1 fork this read PLATE_FOR[pairingOf(r.d)]. */
  const demand = (r, kidsRow) => sectionDemand(kidsRow);
  const mk = (label, kidsRow, rs) => { rs.forEach(r => { r.demand = demand(r, kidsRow); r.pairing = kidsRow ? null : pairingOf(r.d); }); return { label, kidsRow, demand: rs.length ? sectionDemand(kidsRow) : 0, rows: rs, packs: rs.reduce((a, r) => a + r.q, 0) }; };
  if (!H.splitKids) return [mk('', false, rows)].filter(x => x.demand > 0);
  /* On a split table a kids' pack is a Kids-range dish (2026-09-23): the customer now picks from the whole
     list, not only the composed menu, and the kids' list is exactly the Kids range (menuLists). Before
     this the test was kidPackNames(menu), which agreed on every box the menu could build. */
  /* A split table's Coverage is the nights EVERYONE is fed (CONTEXT.md, Coverage). Until 2026-09-24 an empty
     section was dropped here, so grown-up packs alone quoted the grown-ups' nights as the table's; now, once
     the Box holds anything, a section with people and no packs stays in at its own demand and holds the
     Coverage at 0 (Tim's sticky-panel spec: grown-up packs only for two and two reads 0 meals). */
  if (!rows.length) return [];
  return [mk('adult', false, rows.filter(r => !r.d.k)), mk("kids'", true, rows.filter(r => r.d.k))]
    .map(x => x.rows.length ? x : { ...x, demand: sectionDemand(x.kidsRow) }).filter(x => x.demand > 0);
}
/* The optimiser (issue 006, rewritten 2026-09-05 for the ranking): every grown-up dish serves the favourite
   Pairing it supports. It steps down the ranking only when a lower Pairing gains a whole Dinner of Coverage,
   and those rows are marked "stretched" so the menu can say so. Never a Pairing the Household did not rank. */
function optimisePairings(S, dishes, box, menu) {
  const dishesOnTable = menu.table.filter(d => box[d.n] > 0);
  const options = d => { const o = S.pairing.filter(b => d.b.includes(b)); return o.length ? o : [d.b[0]]; };
  const favouriteFirst = {}; dishesOnTable.forEach(d => favouriteFirst[d.n] = options(d)[0]);
  const coverageOf = assign => nightsOf(sections(S, dishes, box, menu, d => assign[d.n] || favouriteFirst[d.n] || d.b[0]));
  const base = coverageOf(favouriteFirst);
  let best = { cov: base, rank: 0, assign: favouriteFirst };
  const walk = (i, assign, rank) => {
    if (i === dishesOnTable.length) {
      const cov = coverageOf(assign);
      if (cov > best.cov || (cov === best.cov && rank < best.rank && cov > base)) best = { cov, rank, assign: { ...assign } };
      return;
    }
    const d = dishesOnTable[i];
    options(d).forEach(b => walk(i + 1, { ...assign, [d.n]: b }, rank + S.pairing.indexOf(b)));
  };
  if (dishesOnTable.length && S.pairing.length > 1) walk(0, {}, 0);
  const stretched = {};
  if (best.cov > base) dishesOnTable.forEach(d => { if (best.assign[d.n] !== favouriteFirst[d.n]) stretched[d.n] = best.cov; });
  return { assign: best.assign, stretched, coverage: best.cov, base };
}
/* The Nudge: the smallest addition that makes Coverage one Dinner higher, found by simulation: Packs are
   added one at a time to the section's first dish (a whole Dinner comes from one dish, so a deeper pack beats a
   new one) until that section reaches the target. */
function nudgeFor(secs) {
  const now = nightsOf(secs);
  if (!secs.length || secs.some(x => !x.rows.length)) return null;   // an empty section has no dish to simulate on
  if (secs.every(x => Math.floor(BLOCKS_PER_PACK / x.rows[0].demand) >= 2)) return null;   // a pack that covers two nights on its own says nothing the track does not
  const target = now + 1;
  const adds = secs.map(x => {
    if (secNights(x) >= target) return 0;
    const rows = x.rows.map(r => ({ ...r }));
    for (let n = 1; n <= 4; n++) { rows[0].q++; if (secNights({ ...x, rows, packs: x.packs + n }) >= target) return n; }
    return null;
  });
  if (adds.some(a => a === null)) return null;
  const parts = secs.map((x, i) => adds[i] ? { packs: adds[i], label: x.label } : null).filter(Boolean);
  return parts.length ? { after: target, parts, text: `Add ${parts.map(p => `${NUM[p.packs] || p.packs} ${p.label ? p.label + ' ' : ''}pack${p.packs === 1 ? '' : 's'}`).join(' and ')} to make it ${target}.` } : null;
}
/* The dinner plan: for each section, one entry a night listing the dish(es) and Blocks that make it. Whole
   dinners are taken from one dish, cycled so every pack gets a turn; then the Dinners the Spare assembles
   across dishes, labelled as such; a pooled section (demand > 8) takes its Blocks from dishes in order. The
   plan is the truth for Coverage: what it can lay is what the customer is told. */
function dinnerPlan(secs) {
  const target = nightsOf(secs);
  const plans = secs.map(x => {
    const rows = x.rows.map(r => ({ d: r.d, left: r.q * BLOCKS_PER_PACK, demand: r.demand, pairing: r.pairing }));
    const nights = [];
    {
      let k = 0, guard = 0;
      while (nights.length < target && guard++ < 128) {
        let tries = 0; while (tries < rows.length && rows[k % rows.length].left < rows[k % rows.length].demand) { k++; tries++; }
        if (tries >= rows.length) break;
        const r = rows[k % rows.length]; r.left -= r.demand; nights.push([{ d: r.d, blocks: r.demand, pairing: r.pairing }]); k++;
      }
      while (nights.length < target) {
        let need = x.demand; const parts = [];
        for (const r of rows) { if (need <= 0) break; const take = Math.min(need, r.left); if (take > 0) { parts.push({ d: r.d, blocks: take, pairing: r.pairing }); r.left -= take; need -= take; } }
        if (need > 0 || !parts.length) break;
        parts.fromSpare = true; nights.push(parts);
      }
    }
    return { section: x, nights, leftovers: rows.filter(r => r.left > 0).map(r => ({ d: r.d, left: r.left, kids: x.kidsRow })) };
  });
  const nights = plans.length ? Math.min(...plans.map(p => p.nights.length)) : 0;
  plans.forEach(p => { p.nights.length = nights; });   // every section lays the same number of Dinners
  return { nights, plans };
}
/* Spare is framed with a use (CONTEXT.md), chosen from the count, the Household (a kids' dinner only when kids
   are at the table, "for you" only when solo) and the dish's own Pairings. First match wins. */
const LEFTOVER_USES = [
  { blocks: 4, kids: true, name: 'another kids’ dinner' },
  { blocks: 4, solo: true, name: 'another dinner for you' },
  { blocks: 4, pairing: 'pasta', name: 'a pasta bake for two' },
  { blocks: 4, name: 'a full plate, on its own' },
  { blocks: 3, pairing: 'rice', name: 'a plate over rice' },
  { blocks: 3, name: 'a loaded jacket' },
  { blocks: 2, pairing: 'wrap', name: 'a lunch wrap' },
  { blocks: 2, name: 'a small bowl for lunch' },
  { blocks: 1, name: 'a slice of toast, topped' }
];
const spareUse = (S, x) => LEFTOVER_USES.find(u => x.left >= u.blocks && (!u.kids || x.kids) && (!u.solo || S.household === 'solo') && (!u.pairing || x.d.b.includes(u.pairing))) || null;

/* --------------------------------------------- the rule (Frequency after the packs, 2026-09-23) */
/* Packs for a Frequency: the rate this Household eats at, over that many weeks, in whole Packs, never
   below Stocked's real minimum order (ADR 0003). Monotone in `weeks`. Because a Plate is 4 Blocks and a
   Pack is 8, this is max(4, ceil(people x meals a week x weeks / 2)). */
const packsFor = (rate, weeks) => Math.max(MIN_PACKS, Math.ceil(rate * weeks / BLOCKS_PER_PACK));
/* The recommendation from the Box actually held (the architect note, Option 1): the offered gap nearest
   to how long these Packs last at this Intention, a tie going to the LONGER gap (a surplus builds up in
   the freezer delivery after delivery; a small shortfall does not), held inside the offered range. */
function recommendFromBox(inBox, perDinner, perWeek) {
  const offered = offeredFrequencies();
  const lasts = inBox * BLOCKS_PER_PACK / perDinner / perWeek;     // weeks
  let best = offered[0];
  for (const w of offered) if (Math.abs(w - lasts) <= Math.abs(best - lasts)) best = w;   // <= : ties to the longer
  return { weeks: best, lasts };
}
/* PARKED 2026-09-27: recommendFromBox is the 4/6/8 rule OLE-17 replaced; kept exported for the record, unread.

   OLE-17, R2: closest in meals a delivery, cards either side (Tim, 27 Sep; clients/stocked/docs/issues/
   design-review-2026-09-22/proof/ole-17/v4/formula-check.md §4-5). The recommendation is the sold plan with the
   smallest |meals - weeks x days a week|: the meals the Box misses at each delivery, short or spare, against the
   days a week the customer answered ("less than once a week" is 0.5). That is the same as the plan nearest to
   how long the Box lasts. A tie goes to the LONGER plan (less food a month; the tie rule the quiz has always
   used). The cards are the recommendation and the sold plans immediately either side of it, the next shorter
   and the next longer; at the shortest or longest sold plan, the two nearest on the one side.
   Why R2 replaced R1, the closest pace (meals / weeks against days a week): the pace error is the meal miss
   divided by the weeks, so the same shortfall counted for less on a longer plan and longer plans looked closer
   than they are. The recommendation barely felt it; the second and third cards did (Charlie's case ranked
   every 3 weeks, 3 meals short, level with every week, 1 spare; two grown-ups at 2 days got 4, 5, 6, where 6
   is 4 meals short). Whole meals, not Blocks, because the card line says "N family meals". `exact` (weeks x
   days a week within half a meal of the meals) is reported for the scores; no card reads it. */
const MISS_TIE = 1e-9;
function rankByMiss(dinners, perWeek, sold) {
  const miss = w => Math.abs(dinners - w * perWeek);                // meals short or spare at each delivery
  return sold.slice().sort((a, b) => (Math.abs(miss(a) - miss(b)) <= MISS_TIE ? b - a : miss(a) - miss(b)));   // tie: the longer first
}
function recommendFromMeals(dinners, perWeek, sold) {
  const weeks = rankByMiss(dinners, perWeek, sold)[0];
  return { weeks, lasts: dinners / perWeek, exact: Math.abs(weeks * perWeek - dinners) <= 0.5 };
}
/* The cards (B33 kept: three): the recommendation and the sold plans either side of it, shortest plan first;
   pinned to the two nearest on one side at either end of what is sold. Every other sold plan is under OLE-16's
   "Select different frequency" link (otherFrequencies with these cards). */
function cardsFor(dinners, perWeek, sold, n = 3) {
  const asc = [...new Set(sold)].sort((a, b) => a - b);
  const at = asc.indexOf(recommendFromMeals(dinners, perWeek, asc).weeks);
  const from = Math.max(0, Math.min(at - Math.floor((n - 1) / 2), asc.length - n));
  return asc.slice(from, from + n);
}

/**
 * frequencyPlan(S, packs, plans) — the target Box and the Frequency recommendation (2026-09-23; OLE-17 27 Sep).
 *
 * Frequency now comes AFTER the packs (Tim, B32; the architect note QZ-frequency-order-architect.md,
 * Option 1). Two things, decided in two places:
 *
 *   THE TARGET, from the answers only. It is the end of the progress bar on the order summary.
 *     1. A Plate is four Blocks for everyone at the table, kids included. A table of N eats 4N a Dinner.
 *     2. The rate is what they plan to eat a week: N x 4 x Intention, in Blocks.
 *     3. For each offered Frequency (every 4, 6 or 8 weeks) the Box is that rate over that many weeks in
 *        whole Packs, never fewer than four.
 *     4. The target is the LONGEST offered Frequency whose Box fits the biggest Box on sale (8), with
 *        that Box. If none fits, the target is 8 Packs at every 4 weeks, `held`, and the cards say
 *        honestly what 8 Packs cover. No capacity handling beyond that (Tim, 2026-09-23): "1 pack feeds
 *        2 people" on the summary lets the customer size a big table themselves.
 *
 *   THE RECOMMENDATION, from the Packs actually in the Box (OLE-17, R2): the sold plan that misses the fewest
 *     meals a delivery (|whole meals - weeks x Intention|, rankByMiss), and the cards are it and the sold plans
 *     either side of it (cardsFor). The target's
 *     weeks no longer decide it. Nothing is ever changed behind the customer's back: the Packs never
 *     move, and a gap they picked (`S.frequency`) sticks while the product sells it; only the Recommended
 *     flag moves.
 *
 * Returns NULL until the Intention and the Household are both answered.
 *
 * Invariants:
 *   I1  `packs` (the target) is a function of the ANSWERS only. `S.frequency` and the Box are never
 *       read for it.
 *   I2  `recommended` is always a sold plan on a card: the Recommended flag is always on a card. `chosen` is
 *       the customer's pick (`S.frequency`) when they made one and the product sells it, else
 *       `recommended`. A pick is a card, or (OLE-16) another sold plan from the "Select different
 *       frequency" link. `override` is true exactly when `chosen` is not on a card, and then no option
 *       carries chosen: true.
 *   I3  MIN_PACKS <= packs <= cap, always, held or not.
 *   I4  `options` is the three cards (`frequencies`) ascending, each reporting what the SAME Box gives.
 *       Exactly one carries recommended: true. `sold` is every plan the product sells.
 *
 * @param {Object} S        a normalised quiz state
 * @param {number} [packs]  the Packs actually in the Box. Omitted, the Box is taken to be on target.
 * @param {Array}  [plans]  the product's selling plans ([{ name }], window.lpQuiz.bundle.sellingPlans in the
 *                          Shopify build). Omitted, the live FREQUENCY_PLANS rows (soldFrequencies).
 */
function frequencyPlan(S, packs, plans) {
  const H = household(S);
  const intention = intentionOf(S);
  if (!intention || !H.answered || !H.people) return null;
  const perWeek = intention.perWeek;
  const rate = H.people * V1_PLATE * perWeek;                    // Blocks a week
  const cap = liveCeiling();
  const offered = offeredFrequencies();
  let targetWeeks = null;
  for (const w of offered) if (packsFor(rate, w) <= cap) targetWeeks = w;   // the longest that fits
  const held = targetWeeks === null;
  if (held) targetWeeks = offered[0];
  const own = held ? cap : packsFor(rate, targetWeeks);          // the target Box (I1)
  const inBox = Number.isFinite(packs) ? packs : own;            // what they actually hold
  /* OLE-68: the meals in the Box count 4 Blocks a grown-up and 2 a kid (blocksPerMeal); the target above keeps
     V1_PLATE for everyone (Q19). Rule A is unchanged: rankByMiss on these meals, a tie to the longer plan. */
  const perDinner = blocksPerMeal(H);
  const dinners = Math.floor(inBox * BLOCKS_PER_PACK / perDinner);
  const sold = soldFrequencies(plans);                           // OLE-17: the product's plans, else the live rows
  const fromBox = recommendFromMeals(dinners, perWeek, sold);
  const recommended = fromBox.weeks;
  const cards = cardsFor(dinners, perWeek, sold);
  const chosen = sold.includes(S.frequency) ? S.frequency : recommended;   // I2
  const override = !cards.includes(chosen);                      // picked from the link, not a card (OLE-16)
  /* The three cards, reporting what this same Box gives. Meals are whole: a card never shows a fraction of
     a meal (the card line is OLE-33's "N family meals"; coverage is kept for the order record's readers). */
  const options = cards.map(w => {
    const cov = coverageLine(dinners, w, perWeek);
    const card = frequencyCardLine(dinners, H.kids, w === recommended ? intention : null);
    return { weeks: w, need: cov.planned, dinners, covers: Math.min(dinners, cov.planned), enough: dinners >= cov.planned,
             recommended: w === recommended, chosen: w === chosen,
             distance: Math.abs(w - recommended),
             coverage: cov, text: cov.text, line: card.count, basis: card.basis };
  });
  return { plate: V1_PLATE, people: H.people, intention: perWeek, intentionId: intention.id,
           intentionLabel: intention.phrase, intentionCard: intention.card, rate, cap, packs: own, targetWeeks, inBox, perDinner, dinners,
           lasts: fromBox.lasts, exact: fromBox.exact, recommended, chosen, override, held, options, frequencies: cards, sold };
}
/* coverageLine(dinners, weeks, intention): what a Frequency card says about THIS Box, in whole numbers
   (Tim, 2026-09-22). The Box is fixed, so the only thing that moves across the cards is how many meals
   that gap plans for: weeks x Intention, rounded UP so half a meal never appears. Three cases, and no
   pack count on the card. Customer copy says "meals" for a Dinner (CONTEXT.md, 2026-09-23):
     equal   "Covers 3 meals for 3 weeks"
     short   "Covers 3 of 4 meals for 4 weeks"
     long    "Covers 3 meals for 2 weeks, 1 spare"
   Pure, so the shipped screen and any Liquid port cannot word it differently. */
function coverageLine(dinners, weeks, intention) {
  const planned = Math.ceil(weeks * intention);
  const wk = `${weeks} week${weeks === 1 ? '' : 's'}`;
  const dn = n => `${n} meal${n === 1 ? '' : 's'}`;
  if (dinners === planned) return { planned, kind: 'equal', text: `Covers ${dn(dinners)} for ${wk}` };
  if (dinners < planned) return { planned, kind: 'short', short: planned - dinners,
    text: `Covers ${dinners} of ${dn(planned)} for ${wk}` };
  const spare = dinners - planned;
  return { planned, kind: 'long', spare, text: `Covers ${dn(planned)} for ${wk}, ${spare} spare` };
}
/* frequencyCardLine(dinners, kids, intention): a frequency card's words (OLE-68; Charlie's Miro "Screen 14",
   capture.md F07; Tim 29 Sep, Q18). `count` is the Box's whole meals, "5 family meals" with kids at the table,
   "5 meals" without, singular at 1, '' at 0 (a card with no whole meal has no count). `basis` is the
   recommended card's echo only (pass its Intention; null for the other cards): ", based on your intended
   2 days/week" after a count, "Based on your intended 2 days/week" alone. Pure, one source for the words. */
/* OLEO-154 (Tim, 6 Oct, from Other Charlie's note on the plan cards): on V2A and V2B every card reads "4 meals per
   person" ("per person" replaces "family", as the count is each person's), and the recommended card "4 meals per
   person, based on your intended 2 days/week"; a "less often" answer has no "based on" clause. Today's quiz is live's. */
/* OLEO-174 (Tim's 7 Oct grill Q2, Q3, Q14; 06-decisions.md #2): the clause echoes the intention screen's words,
   "based on your intended 1 day a week" (`phrase`), not the "1 day/week" shorthand; the count is perPersonCount's. */
function frequencyCardLine(dinners, kids, intention, variant = VARIANT) {
  if (v11(variant)) {
    const count = perPersonCount(dinners);
    const basis = !intention || intention.id === 'less' ? '' : `${count ? ', based' : 'Based'} on your intended ${intention.phrase}`;
    return { count, basis, text: count + basis };
  }
  const count = dinners > 0 ? `${dinners} ${kids > 0 ? 'family ' : ''}meal${dinners === 1 ? '' : 's'}` : '';
  const basis = !intention ? '' : `${count ? ', based' : 'Based'} on your intended ${intention.card}`;
  return { count, basis, text: count + basis };
}
/* OLEO-174 · Quiz V2's meals number, one source for the menu bar and the frequency cards (Tim's 7 Oct grill Q2, Q3,
   Q14; 06-decisions.md #2). N is the Dinners the Box covers from one shared stock of Blocks for the Household: packs x 8
   over the table's Blocks a meal (4 an adult, 2 a kid), whole, rounded down. It is frequencyPlan's `dinners` for the
   same Box, the same formula (tested), so the bar and every card read the same N. Never "Total Meals", never "family
   meals". perPersonCount(n): "N meals per person", singular at 1, '' at 0. */
const perPersonCount = n => n > 0 ? `${n} meal${n === 1 ? '' : 's'} per person` : '';
const dinnersInBox = (S, packs) => { const H = household(S); return H.people > 0 && packs > 0 ? Math.floor(packs * BLOCKS_PER_PACK / blocksPerMeal(H)) : 0; };
/* perPersonBar(S, packs): the V2 menu bar's meal line. Under the 4-pack Minimum, or a Box with no whole meal, the bar
   keeps "1 pack feeds 2 people" (`feeds` true, count ''); from the Minimum, "N meals per person". */
function perPersonBar(S, packs) {
  const dinners = dinnersInBox(S, packs), feeds = packs < MIN_PACKS || !dinners;
  return { packs, dinners, feeds, count: feeds ? '' : perPersonCount(dinners) };
}

/* The order record (Tim, 2026-09-22, option (c) of quiz-design-c.md): thirteen flat strings, written as
   a SNAPSHOT at order time. Derived values are written out rather than left to be re-derived, because the
   reader is a marketer in a Klaviyo segment builder with no access to this rule. `quiz_meals` and
   `quiz_cadence` are renamed rather than kept, which is safe because no live order carries them yet.
   Pure: state and plan in, strings out. */
/* Amended 2026-09-23 (the design review): quiz_household and quiz_freezer are gone (the household types
   and the freezer question are parked); the Household is written as its two counts; quiz_intention is
   '1'..'5' or 'less'; quiz_freq_recommended is the gap the Frequency step flagged, which is now derived
   from the Packs in the Box; quiz_packs_recommended is the target the answers set.
   Amended 2026-09-23 (Tim, QZ-10): five more answers, then quiz_kept, quiz_bound and quiz_coverage_balance
   dropped: FIFTEEN keys, still flat strings. Each is the value
   the state stores, never a relabel:
     quiz_reason      S.problem, the reason key: time | scratch | healthy | deciding (QZ-19, 24 Sep) | spending
                      (OLE-7, 25 Sep), or 'unanswered'.
                      A saved state from before 24 Sep may still carry a parked key (cantface | takeaway | upf | recipebox)
     quiz_diet        S.diet: meat | plant | mix (both ticked) (or 'unanswered')
     quiz_spice       the grown-ups' slider, 0..3 (0 not spicy, 3 spicy); the slider opens at 1 (C10, 25 Sep; was 2)
     quiz_spice_kids  the kids' slider, 0..3, or 'none' when there are no kids. When the kids eat the same
                      meal there is one slider for everyone (24 Sep), and this equals quiz_spice
     quiz_kids_same   S.kidsSplit: yes (same) | no (own) | none (no kids) | 'unanswered' */
function answerAttributes(S) {
  const H = S ? household(S) : null, kids = H && H.answered && H.kids > 0;
  const level = a => String(Math.max(...(Array.isArray(a) && a.length ? a : [0])));
  return {
    quiz_reason: S && S.problem ? String(S.problem) : 'unanswered',
    quiz_diet: S && S.diet ? S.diet : 'unanswered',
    quiz_spice: S ? level(S.heatAdult) : 'unanswered',
    quiz_spice_kids: !H || !H.answered ? 'unanswered' : kids ? level(kidsHeat(S)) : 'none',
    quiz_kids_same: !H || !H.answered ? 'unanswered' : !kids ? 'none' : S.kidsSplit === 'same' ? 'yes' : S.kidsSplit === 'own' ? 'no' : 'unanswered'
  };
}
/* quiz_purchase (freq-preview, Tim 2026-09-24): the purchase type chosen on the Frequency screen's sticky, as
   the state stores it: sub (Subscribe & Save, the default) | once (one-off). A one-off is delivered once, so
   its quiz_freq_chosen is 'none'; quiz_freq_recommended is still written, because the recommendation was made.
   SIXTEEN keys.
   Amended 2026-09-30 (Tim, OLE-95): three keys renamed for a reader at a glance, values unchanged:
   quiz_people -> quiz_household_size, quiz_packs_chosen -> quiz_number_packs_chosen, quiz_purchase ->
   quiz_purchase_type. Two dropped: quiz_packs_recommended (the quiz never shows a pack recommendation) and
   quiz_intention (quiz_intention_label carries the same answer). FOURTEEN keys. Orders placed before the
   OLE-95 push keep the old names. */
const purchaseOf = S => S && S.purchase === 'once' ? 'once' : S ? 'sub' : 'unanswered';
/* ctaPrice(S, q): the Frequency sticky's CTA price, read off quote() for the same Box. paid is q.payable
   (packs x £9.50 on Subscribe & Save, packs x £11.05 one-off, both less BOX_DISCOUNT at 8 packs, less any
   offer code); struck is the one-off price on Subscribe & Save only, never on one-off (Tim, 24 Sep). */
function ctaPrice(S, q) {
  const sub = purchaseOf(S) === 'sub';
  return { purchase: sub ? 'sub' : 'once', paid: q.payable, struck: sub && q.packs > 0 && q.payable < q.oneOff ? q.oneOff : null };
}
/* OLEO-178 (Tim, 7 Oct: "more elegant to change v1 to v2 for quiz"): the record's `quiz` is the quiz version, on the
   order and on the events that carry the record. Quiz V2 writes "v2" (the dropped V2B "v2b"); today's quiz keeps "v1",
   exactly as live. `variant` defaults to the page's template (VARIANT), so every caller gets the page's version. */
const quizVersion = v => variantName(v) || 'v1';
function cohortAttributes(S, plan, variant = PAGE_VARIANT) {   // OLEO-220: the page's version ("v3" on Quiz V3; VARIANT elsewhere)
  const H = S ? household(S) : null;
  const count = n => H && H.answered ? String(n) : 'unanswered';
  if (!plan) return {
    quiz: quizVersion(variant), quiz_intention_label: 'unanswered',
    quiz_adults: count(H && H.adults), quiz_kids: count(H && H.kids), quiz_household_size: count(H && H.people),
    quiz_freq_recommended: 'unanswered', quiz_freq_chosen: 'unanswered',
    quiz_number_packs_chosen: 'unanswered',
    quiz_purchase_type: purchaseOf(S),
    ...answerAttributes(S)
  };
  /* quiz_kept, quiz_bound and quiz_coverage_balance were removed from the record on 2026-09-23 (Tim): the
     record keeps the answers and the recommended / chosen packs and gap, which let any of the three be
     re-derived later. Nothing else computed them. */
  return {
    quiz: quizVersion(variant),
    quiz_intention_label: plan.intentionLabel.toLowerCase(),
    quiz_adults: String(H.adults),
    quiz_kids: String(H.kids),
    quiz_household_size: String(plan.people),
    quiz_freq_recommended: String(plan.recommended),
    quiz_freq_chosen: purchaseOf(S) === 'once' ? 'none' : String(plan.chosen),
    quiz_number_packs_chosen: String(plan.inBox),
    quiz_purchase_type: purchaseOf(S),
    ...answerAttributes(S)
  };
}

/* mealsFor(S, packs, q): the education card's line under the header card (moved there from the Box bar,
   Tim 2026-09-24, sticky-panel spec). Tim, 2026-09-24: "once they've added a pack, it should say this is one meal for two grown-ups and two kids, or half a meal ... just to track their progress").
   One pack feeds two people, so meals = packs × 2 ÷ people, rounded DOWN to the nearest half: a family of
   four reads ½ meal, 1 meal, 1½ meals as it taps. A table where one pack is under half a meal (5 or more
   people) reads "Part of a meal" until it reaches ½. At 0 packs there is no line (count ''); the card
   reads "💡 1 pack feeds 2 people." instead. On a shared table the whole meals equal quote().coverage (tested);
   halves are the progress between them. `count` is plain text, the caller bolds it. */
/* A SPLIT table (Tim, 2026-09-24) counts only the meals where everyone is fed, in whole meals: the count is
   quote().coverage, so pass the quote (q) for the same Box. Grown-up packs alone read 0 meals, and the card
   says what is missing ("Add kids’ dishes to feed the whole table."; kids' packs alone, the grown-ups'). Both
   sections held but short of a whole meal reads "Part of a meal". A shared table keeps the halves. */
/* C13 (Charlie's Paper comment, Tim 25 Sep): the meals line says what a meal is, as secondary text after the
   sentence: "3 meals for three adults and two kids (based on 4 Blocks per meal)". `basis` is that parenthetical
   (index.html sets it in the muted style); `text` is the whole line. The 4 is V1_PLATE.
   OLE-68 (Tim, 29 Sep, Q14): a table with kids counts 4 Blocks a grown-up and 2 a kid (blocksPerMeal), and its
   basis says so, "(based on 4 Blocks per adult, 2 per kid)"; a table without kids keeps MEAL_BASIS. The halves
   are packs × 8 × 2 ÷ blocksPerMeal, floored: two adults and two kids (12 Blocks) read ½, 1, 2, 2½ … as they tap. */
const MEAL_BASIS = `(based on ${V1_PLATE} Blocks per meal)`;
const MEAL_BASIS_KIDS = `(based on ${V1_PLATE} Blocks per adult, ${KID_BLOCKS} per kid)`;
const mealBasis = H => H.kids > 0 ? MEAL_BASIS_KIDS : MEAL_BASIS;
function mealsFor(S, packs, q) {
  const H = household(S), people = Math.max(1, H.people), basis = mealBasis(H), perMeal = Math.max(1, blocksPerMeal(H));
  if (H.splitKids && packs > 0) {
    if (!q || !Array.isArray(q.sections)) throw new Error('mealsFor: a split table counts from the quote; pass quote() for the same Box');
    const held = kidsRow => q.sections.some(x => x.kidsRow === kidsRow && x.packs > 0);
    const missing = !held(true) ? 'kids' : !held(false) ? 'grown-ups' : null, whole = missing ? 0 : q.coverage;
    const count = missing ? '' : !whole ? 'Part of a meal' : `${whole} meal${whole === 1 ? '' : 's'}`;
    const text = missing === 'kids' ? 'Add kids\u2019 dishes to feed the whole table.'
               : missing ? 'Add adult dishes to feed the whole table.' : `${count} for ${tableLine(S)} ${basis}`;
    return { packs, people, meals: whole, whole, half: false, part: !missing && !whole, split: true, missing, count, basis: missing ? '' : basis, text };
  }
  const halves = packs > 0 ? Math.floor(packs * BLOCKS_PER_PACK * 2 / perMeal) : 0;   // meals × 2, floored: to the half below
  const whole = Math.floor(halves / 2), half = halves % 2 === 1;
  const count = !packs ? '' : !halves ? 'Part of a meal'
              : `${whole || ''}${half ? '½' : ''} meal${halves > 2 ? 's' : ''}`;
  return { packs, people, meals: halves / 2, whole, half, part: packs > 0 && !halves, split: false, missing: null, count,
           basis: count ? basis : '', text: count ? `${count} for ${tableLine(S)} ${basis}` : '' };
}

/* quote: everything the menu, the sticky bar, the sheet and the box show, from one call. The menu's Dinner
   prices, the Spare and the Box price reconcile by construction: dinners × Blocks + Spare Blocks = packs × 8.
   Since the box discount (2026-09-24) they reconcile to listTotal, the Subscribe & Save price before it;
   total is listTotal less BOX_DISCOUNT at 8 packs. */
/* opts.prefill === false (2026-09-23, the V1 order summary): an untouched empty Box stays empty, because
   the customer now fills it from the menu toward the target. The default still opens the Box at the
   target from the composed menu, which is what the parked screens and the invariant tests exercise. */
function quote(S, dishes, opts = {}) {
  const menu = opts.menu || composeMenu(S, dishes);
  const box = Object.keys(S.box).length ? S.box : (S.boxTouched || opts.prefill === false ? {} : openBox(S, dishes, menu));
  const H = household(S), D = dinnerFor(S);
  const opt = optimisePairings(S, dishes, box, menu);
  const pairingOf = d => opt.assign[d.n] || S.pairing.find(b => d.b.includes(b)) || d.b[0];
  const secs = sections(S, dishes, box, menu, pairingOf);
  const plan = dinnerPlan(secs);
  const packs = Object.values(box).reduce((a, b) => a + b, 0);
  const blocks = packs * BLOCKS_PER_PACK;
  /* BOX_DISCOUNT: from the threshold on (8 is also the fill ceiling, so in practice exactly 8), both prices
     take the builder's 10%, rounded to the penny on the Box total as the builder shows it. */
  const boxDiscount = packs >= BOX_DISCOUNT.packs ? BOX_DISCOUNT.pct : 0, less = x => penny(x * (100 - boxDiscount) / 100);
  const listTotal = packs * PACK_PRICE;              // before the box discount: what the Dinner prices and the Spare reconcile to
  const total = less(listTotal), oneOff = less(packs * ONE_OFF_PACK_PRICE);
  const price = S.purchase === 'sub' ? total : oneOff;
  const offerOff = S.offer ? price * S.offer.pct / 100 : 0;
  const dinnerBlocks = i => plan.plans.reduce((a, p) => a + (p.nights[i] || []).reduce((b, x) => b + x.blocks, 0), 0);
  const dinnerPrices = Array.from({ length: plan.nights }, (_, i) => BLOCK_PRICE * dinnerBlocks(i));
  const leftovers = plan.plans.flatMap(p => p.leftovers.map(x => ({ d: x.d, left: x.left, kids: x.kids, use: spareUse(S, x) })));
  const spare = leftovers.reduce((a, x) => a + x.left, 0);
  const cap = CAP[S.freezer] || null;
  return {
    menu, box, packs, blocks, total, oneOff, price, offerOff, payable: price - offerOff, boxDiscount, listTotal, blockPrice: BLOCK_PRICE,
    packPrice: PACK_PRICE, oneOffPackPrice: ONE_OFF_PACK_PRICE,
    dinner: D, household: H,
    coverage: plan.nights, minimum: minimumFor(S, dishes, menu), big: sectionsBig(S),
    spare, leftovers,
    /* V1: the frequency block the Box sheet reads, from the same one quote as every other number on it.
       opts.plans (OLE-17): the product's selling plans, which index.html passes from window.lpQuiz.bundle in the
       Shopify build; omitted, the live rows. */
    frequency: frequencyPlan(S, packs, opts.plans),
    nudge: nudgeFor(secs),
    sections: secs, plan,
    pairingOf, stretched: opt.stretched, coverageFavourite: opt.base,
    dinnerBlocks, dinnerPrices,
    dinnersPrice: dinnerPrices.reduce((a, b) => a + b, 0),
    sparePrice: BLOCK_PRICE * spare,
    cap, overCap: !!cap && packs > cap
  };
}

/* ---------------------------------------------------------------- offer screen */
/* OLEO-161 (Tim, 5-6 Oct; approved 6 Oct, Ready to Push): the box-offer screen between the education screen
   (`versatile`) and the menu. Every number on it is read from here, never typed (ADR 0003):
     offerLadder(charge)   the three Milestones that carry a Pack count (MILESTONES: 4 Box, 6 free delivery,
                           8 extra BOX_DISCOUNT.pct), each with its price per Pack on Subscribe & Save (PACK_PRICE,
                           less BOX_DISCOUNT from its threshold) and, below the free-delivery Milestone, the delivery
                           charge in pounds (`charge` is pence, the section's settings.delivery_cost_not_free).
                           `struck` marks the free-delivery step, where that same charge is shown crossed out.
                           Checked 5-6 Oct: Shopify's shipping rates for a 4-pack cart are "£6 Delivery", 6.00,
                           one-off and Subscribe & Save alike, and "Free Delivery" at 6 packs.
     fullMenuCount(S, d)   the dishes the menu's full list shows this household before any filter: the adults'
                           range, plus the kids' range when there are kids at the table (split: menuLists().kids;
                           shared: kidsRange), the rule lp-quiz.js omSectionsC uses. 34 with kids, 26 without (5 Oct).
   No tier is chosen on the screen and the menu's target is unchanged (Tim, 5 Oct round 2: "Just let them continue"). */
function offerLadder(chargePence) {
  const charge = chargePence > 0 ? chargePence / 100 : null;
  const freeAt = milestone('delivery').packs;
  return MILESTONES.filter(m => m.packs).map(m => ({
    id: m.id, packs: m.packs, emoji: m.emoji,
    perPack: penny(PACK_PRICE * (100 - (m.packs >= BOX_DISCOUNT.packs ? BOX_DISCOUNT.pct : 0)) / 100),
    delivery: charge != null && m.packs < freeAt ? charge : null,
    struck: charge != null && m.id === 'delivery' ? charge : null
  }));
}
function fullMenuCount(S, dishes) {
  const L = menuLists(S, dishes), H = household(S);
  const kids = H.splitKids ? L.kids : (H.kids > 0 ? L.kidsRange : []);
  return L.table.length + kids.length;
}

/* ----------------------------------------------------------------------- flow */
/* The V1 order after the 22 Sep design review (Tim, 2026-09-23):
     Reason → Household → Intention → Diet → Spice → Kids same-food (kids only) → Education → packs → Frequency
   `problem` moved to the front (the reason question opens any quiz), the education is one screen after the
   answers (`versatile`), and Frequency is the LAST screen, after the packs, carrying the checkout.
   Every parked screen keeps a place in this array so the change reads as a parking, not a deletion.
   Amended 2026-09-23 (Tim with Charlie, Slack): the Kids same-food question moves up to straight after the
   Household, so the order is Reason → Household → Kids same-food (kids only) → Intention → Diet → Spice →
   Education → packs → Frequency. The Spice screen reads the kids count, not this answer, so it is unaffected. */
const FLOW = ['problem', 'scale', 'who', 'kidsplit', 'seen', 'blocks', 'portion', 'intention', 'resolution',
              'cooktime', 'orderback', 'i1', 'diet', 'allergens', 'heat', 'i2', 'freezer', 'pairing',
              'duels', 'versatile', 'offer', 'email', 'menu', 'box', 'frequency'];
/* OLEO-220 (Quiz V3 only): `email`, the Email capture screen, follows `versatile` and comes before Build your menu
   (`box`), on both routes. It asks something, so it is not a result: it is a progress mark (the 7th for a table
   without kids). lp-quiz.js takes it out of the flow for a visitor Klaviyo already knows (lp-quiz-email.js). */
/* OLEO-161: `offer` (the box-offer screen) follows `versatile`. It asks nothing, so it is a result: the progress
   marks read complete on it, as on the menu, and quiz_step still names it (step "offer") for its drop-off. */
const RESULTS = new Set(['offer', 'menu', 'box', 'frequency']);  // the order: past every question mark
/* Parked: built, still rendered (?force=<key> on the board), but out of the flow. i1 is the desire-deepening
   card, i2 the objection card; allergens, pairing, cooktime, orderback and menu were dropped on 2026-09-21;
   seen, blocks, portion, scale, resolution, freezer and duels on 2026-09-23 (the design review). None is
   deleted. */
const PARKED = new Set(['i1', 'i2', 'allergens', 'pairing', 'cooktime', 'orderback', 'menu',
                        'seen', 'blocks', 'portion', 'scale', 'resolution', 'freezer', 'duels']);
/* The problem keys that carry a scale ladder. A contract with SPA and the interstitial copy in index.html:
   reword a label freely, never rename a key. `noenergy` is retired from the V1 problem screen (folded into
   `cantface`) but stays here so a board card seeded with it still renders its ladder. */
const SCALE_PROBLEMS = new Set(['cantface', 'takeaway', 'noenergy', 'recipebox', 'waste', 'upf']);
/* Intention is ASKED, never inferred. The ladder-derived suggestion (COUNT_LADDERS, suggestedMeals)
   was deleted on 2026-09-22: a struggle score is not a plan, and 73% of cancellers never meant to use
   Stocked weekly (Charlie Cooke's survey), so a preselect derived from the ladder pushed them the wrong
   way. The Intention screen opens on nothing. */

/* flow(S, dishes): the screens this state will see. `dishes` is optional and only the tasting needs it —
   a pool filtered down to nothing has no tasting to offer, and the screen threw on `pairs[i]` rather than
   being skipped (2026-09-22). Called without dishes the tasting is assumed to be runnable, which keeps
   every existing caller working. */
function flow(S, dishes, variant = PAGE_VARIANT) {
  const H = household(S);
  return FLOW.filter(k => {
    if (PARKED.has(k)) return false;
    if (k === 'offer') return v11(variant) === 'b';                         // OLEO-154: the offer screen is V2B's only
    if (k === 'email') return isV3(variant);                                // OLEO-220: the Email capture screen is Quiz V3's only
    if (k === 'scale' || k === 'resolution') return SCALE_PROBLEMS.has(S.problem);
    if (k === 'kidsplit') return H.kids > 0;
    if (k === 'blocks' || k === 'portion') return S.seen !== 'returning';   // the two explainers; a returning customer skips both
    if (k === 'duels') return !dishes || duelRounds(S, dishes) > 0;
    return true;
  });
}
const markSteps = (S, dishes, variant = PAGE_VARIANT) => flow(S, dishes, variant).filter(k => !RESULTS.has(k));

/* ----------------------------------------------------------------------- copy */
/* From the two counts (2026-09-23); the carer wording is parked with the carer answer. */
function tableLine(S) {
  const H = household(S), num = n => NUM[n] || n;
  if (H.adults === 1 && !H.kids) return 'you';
  if (H.adults === 2 && !H.kids) return 'the two of you';
  return `${num(H.adults)} adult${H.adults === 1 ? '' : 's'}${H.kids ? ` and ${num(H.kids)} kid${H.kids === 1 ? '' : 's'}` : ''}`;
}
/* Footprint: the Box against the freezer they described. */
function footprintLine(S, packs) {
  const cap = CAP[S.freezer]; if (!cap) return '';
  const room = ({ tiny: 'the freezer above the fridge', drawer: 'a drawer', half: 'half a freezer', chest: 'a chest freezer' })[S.freezer];
  if (packs > cap) return `${packs} packs is more than ${room} usually takes (about ${cap}).`;
  const f = packs / cap;
  return `${packs} packs is ${f >= 0.95 ? 'about all of' : f >= 0.7 ? 'most of' : f >= 0.45 ? 'about half of' : f >= 0.3 ? 'about a third of' : 'a corner of'} ${room}.`;
}
/* The line that joins the menu to the box: what the packs became, and what they cost. */
function reconcileLine(q, opts = {}) {
  const n = q.coverage, d = `${n} dinner${n === 1 ? '' : 's'}`;
  if (opts.prices === false) return `Your ${q.packs} packs: ${d}${q.spare ? ` plus ${q.spare} Block${q.spare === 1 ? '' : 's'} left over` : ''}.`;   // the menu carries no prices (Tim, 2026-09-06)
  const sp = q.spare ? ` plus ${q.spare} Block${q.spare === 1 ? '' : 's'} left over (${gbp(q.sparePrice)})` : '';
  return `Your ${q.packs} packs: ${d} at ${gbp(q.dinnersPrice)}${sp}. ${gbp(q.total)} with Subscribe & Save, ${gbp(q.oneOff)} one-off.`;
}

return {
  PACK_PRICE, ONE_OFF_PACK_PRICE, BLOCKS_PER_PACK, BLOCK_PRICE, MIN_PACKS, BOX_DISCOUNT, MILESTONES, milestoneFor, CAP, PAIRINGS, PLATE_FOR, NUM, gbp,
  V1_PLATE, KID_BLOCKS, blocksPerMeal, INTENTIONS, FREQUENCY_PLANS, PACK_CEILING, liveFrequencies, offeredFrequencies, liveCeiling, packsFor,
  pickableFrequencies, otherFrequencies, soldFrequencies, planName, planWeeks,
  RULE_IN_TWO_SENTENCES, RULE_WORKED_EXAMPLES, frequencyPlan, recommendFromBox, recommendFromMeals, cardsFor, rankByMiss, coverageLine, frequencyCardLine, cohortAttributes, answerAttributes, ctaPrice, FOR_YOU_DISHES, SCALE_PROBLEMS,
  defaults, SCHEMA_VERSION, normalise, MAX_AT_TABLE, REASON_KEYS, ensureReasonOrder,
  household, kidsHeat, setHeat, setKidsSplit, applyAnswer, keepOnMenu, packsPerTap, packsPerDish, PACKS_PER_TAP, menuLists, nearLevel, dinnerFor, maxHeat,
  OVERFILL_MODES, BOX_OVERFILL, tapBox, boxBar, packsIn, forYouCount, forYouList, HEAT_LEVELS, heatTicks, DIET_TYPES, dietTicks, dietOf, menuFilter,
  BUILDER_LISTED, BUILDER_LISTED_CAPTURED, builderListed,
  PAIRING_ALLERGENS, pool, grownPool, kidPool, pairingAllowed, pairingOptions, byName,
  duelSets, duelPairs, duelRounds, DUEL_ROUNDS, composeMenu, kidPackNames,
  rank, pairs, score, reasonFor, TASTE_WEIGHTS, spiceBand,
  openBox, minimumFor, sectionsBig, boxPlan, quote, mealsFor, perPersonCount, dinnersInBox, perPersonBar, MEAL_BASIS, MEAL_BASIS_KIDS, mealBasis, spareUse,
  offerLadder, fullMenuCount, V11_VARIANTS, v11, VARIANT, variantName, quizVersion, FOR_YOU_DISHES_LIVE,
  V3_VARIANT, isV3, PAGE_VARIANT,
  FULL_BOX_NOTE, sheetHistory,
  FLOW, RESULTS, PARKED, flow, markSteps,
  tableLine, footprintLine, reconcileLine
};
});
